import * as React from "react";
import { isQrChat, hasMetaServiceWindow } from "../../../utils/platformFamily";
import ChatNumberBadge from "./ChatNumberBadge";
import {
  List,
  ListItem,
  ListItemAvatar,
  Avatar,
  Typography,
  Box,
  Chip,
  Skeleton,
  Tooltip,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  useTheme,
  useMediaQuery,
  InputBase,
  CircularProgress,
} from "@mui/material";
import PushPinOutlinedIcon from "@mui/icons-material/PushPinOutlined";
import BlockIcon from "@mui/icons-material/Block";
// Pin and Block stay MUI — iconsax's ~995-icon set has no thumbtack or
// circle-slash equivalent (confirmed by listing the actual package, not
// guessed), and Rahim confirmed keeping MUI here rather than a wrong-shaped
// substitute (2026-08-19). Everything else in this menu uses iconsax, our
// standard icon library — see icon-library-iconsax memory (consolidated
// into the existing iconsax-react import further below, not duplicated).
import { useSelector, useDispatch, useStore } from "react-redux";
import {
  selectUserDetailsData,
  selectInboxRecentData,
  selectInboxRecentLoading,
  selectInboxRecentError,
  selectInboxRecentInitialized,
  selectInboxRecentLazyTabsLoaded,
  selectSelectedInboxTab,
  selectContactID,
  selectContactInfo,
  checkContactExistsInInboxData,
} from "../../../redux/selectors";
import {
  dispatchInboxSidebarTabChangeClear,
  navigateOffCustomerChatRoute,
} from "../../../utils/inboxTabSwitch";
import { fetchUserDetails } from "../../../redux/Slice/User/userDetailsSlice";
import { fetchInbox, fetchClosedTabInbox, updateAssignmentInInbox, updateLifecycleStageInInbox, deleteChat, deleteContact, togglePinContact, blockContact, unblockContact, deleteGroupChat, toggleGroupPin } from "../../../redux/Slice/Inbox/inboxRecentSlice";
import { setSelectedInboxTab, markSkipNextTabChangeClear, clearSkipTabAutoCorrect } from "../../../redux/Slice/Inbox/inboxSlice";
import LifecycleStageChip from "../../Lifecycle/LifecycleStageChip";
import { DARK_BUBBLE_TOOLTIP_SX } from "../../../utils/tooltipStyles";
import IconButtonWithTooltip from "../Shared/IconButtonWithTooltip";
import LifecycleStageMenuList from "../../Lifecycle/LifecycleStageMenuList";
import { reportsDropdownSelectedSx } from "../../Reports/reportsFilterStyles";
import { invalidateCachedContact } from "../../../redux/Slice/Inbox/inboxInfoSlice";
import { clearUrlContactSyncSuppress } from "../../../redux/Slice/Inbox/inboxAllSlice";
import { nameToContactAvatarImage, NON_DRAGGABLE_AVATAR_IMG_PROPS, stringToColor } from "../../../config/AvatarColor";
import axios from "axios";
import { getAuthToken } from "../../../utils/getAuthToken";
import { checkBeforeOutbound } from "../../../utils/subscriptionError";
import { setAssignToAiLoading } from "../../../redux/Slice/Inbox/aiAssistStatusSlice";
import { fetchAiAgents } from "../../../redux/Slice/AiAgents/aiAgentsSlice";
// Same custom "ai-users" glyph ChatboxNavbar.jsx's own "Assign to AI" button uses
// (Iconsax premium icon, not in the free iconsax-react package — see that file's
// own comment on why this is a real .svg import, not inline JSX).
import { ReactComponent as AiUsersIcon } from "../../../Images/Inbox/AiUsers.svg";
import { formatMessageTime } from "../../../utils/timestampUtils";
import { formatMessageCardContactDisplayName } from "../../../utils/formatMessageCardContactDisplayName";
import { formatBadgeCount } from "../../../utils/formatBadgeCount";
import CloseIcon from "@mui/icons-material/Close";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useContactSelection } from "../../../hooks/useContactSelection";
import { useSubscriptionGuard } from "../../../hooks/useSubscriptionGuard";
import { getErrorMessage } from "../../../utils/errorHandler";
import { FixedSizeList as VirtualList } from "react-window";
import PlatformIcon from "./PlatformIcon";
import { ArrowUp, ArrowDown2, ArrowRight2, Trash as TrashIcon, UserRemove as UserRemoveIcon, Unlock as UnlockIcon, RefreshCircle as LifecycleIcon, Profile2User as AssignIcon, SearchNormal as SearchIcon } from "iconsax-react";
import { fetchInboxTeamData, assignContact } from "../../../redux/Slice/Inbox/inboxSettingsSlice";
import { fetchLifecycleStages, moveContactLifecycleStage } from "../../../redux/Slice/Lifecycle/lifecycleStageSlice";
import { DROPDOWN_PAPER_SX, DROPDOWN_ITEM_SX } from "../../../utils/dropdownMenuStyles";
import { resolveDisplaySender } from "../../../utils/resolveDisplaySender";
import { classifyLastMessageType, formatLastMessagePreview } from "../../../utils/formatLastMessagePreview";
import toast from "react-hot-toast";
import BulkCheckbox from "./BulkCheckbox";
import { alpha } from "@mui/material/styles";
import {
  toggleBulkSelection,
  clearBulkSelection,
  setBulkSelectableIds,
  selectIsBulkSelected,
  selectBulkSelectionCount,
  selectBulkSelectionActive,
  MAX_BULK_SELECTION,
} from "../../../redux/Slice/Inbox/inboxBulkSelectionSlice";

// Context that carries the currently-open menu contact ID.
// Only the one row whose contactId matches re-renders when the menu opens/closes.
const MenuOpenContext = React.createContext(null);

// Hover tracking — deliberately NOT plain CSS `:hover`. This list is
// virtualized (react-window) and sorted "Newest first", so an incoming
// message can reorder the array and slide a DIFFERENT contact's row into
// the exact screen position the cursor is still sitting on (react-window
// preserves each row's DOM node across a reorder via itemKey=contactId — it
// just repositions it, no unmount). A pure `top` style change on an existing
// element does NOT make the browser re-run hit-testing on its own; that only
// happens on a real mouse event. So CSS `:hover` (and, equally, JS
// onMouseEnter/onMouseLeave — same limitation) can go stale: a row that slid
// away keeps showing hover styling, or the row that slid into the cursor's
// position doesn't get it, until the mouse physically twitches. That's the
// reported glitch — the dropdown arrow/timestamp swap misfiring, and the
// lifecycle chip's hover-tint (background.default, a near-white gray)
// reading as "the pill turns white" on the wrong row (Rahim, 2026-09-09,
// re-raising the long-deferred "issue #2" from earlier this session).
//
// Fix: compute "which row is under the cursor" ourselves from real
// coordinates (mouse Y vs. each row's known position) instead of trusting
// the browser's hover pseudo-class, AND recompute it whenever the list
// reorders (using the last known mouse position), not just on mouse
// movement — see hoveredContactIdRef/lastMouseClientYRef in MessageCard
// below.
//
// This is a tiny external store (subscribe/getSnapshot), not React state on
// a Context VALUE — a plain `<Context.Provider value={hoveredContactId}>`
// would re-render EVERY visible row (10-14 of them, react-window keeps
// several mounted at once) on every single hover-target change, since
// Context propagation bypasses React.memo entirely; only 1-2 rows ever
// actually need to re-render (Rahim, 2026-09-09: "the hover is taking too
// long... before it was very fast" — confirmed live, not just a feeling).
// The Context here instead carries the STORE OBJECT, which never changes
// reference, so providing it costs nothing; each row calls
// useSyncExternalStore with its OWN selector (message.contactId ===
// hoveredContactId) so React only re-renders a row when THAT row's own
// boolean actually flips.
function createHoverStore() {
  let hoveredContactId = null;
  const listeners = new Set();
  return {
    getHoveredContactId: () => hoveredContactId,
    setHoveredContactId: (id) => {
      if (id === hoveredContactId) return;
      hoveredContactId = id;
      listeners.forEach((listener) => listener());
    },
    subscribe: (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
}
// Used for the non-virtualized error-fallback render path (react-window
// itself threw) — no reorder-under-a-stationary-cursor problem to solve
// there, so a store that never reports anything hovered is fine.
const NOOP_HOVER_STORE = { getHoveredContactId: () => null, subscribe: () => () => {} };
const HoveredRowContext = React.createContext(NOOP_HOVER_STORE);

// Popup-size standard, confirmed 2026-08-19 by checking BOTH Dashboard's
// FilterDropdown.jsx (DashboardScratch/components/) and every Reports dropdown
// (ReportsTable.jsx) directly — they're NOT the same: Reports uses bigger numbers
// (0.9rem/py:0.95/px:1.625/16px radius), Dashboard uses smaller/tighter ones.
// First pass matched Dashboard's tighter numbers, but this menu (the "..."
// menu + its Lifecycle/Assign submenus) read too small in practice — bumped to
// Reports' bigger formula instead (Rahim, 2026-08-19). Overridden here on top
// of the shared DROPDOWN_*_SX base rather than in dropdownMenuStyles.js itself,
// since the Lifecycle chip's own popover and the filter dropdown still use the
// smaller Dashboard-matched numbers and weren't asked to change.
const REPORTS_MENU_ITEM_SX = { ...DROPDOWN_ITEM_SX, fontSize: "0.9rem", py: 0.95, px: 1.625, mx: 0.5 };
const MENU_PAPER_SX = { ...DROPDOWN_PAPER_SX, borderRadius: "16px" };
const MENU_ICON_BOX_SX = { width: 20, height: 20, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 };

// Every anchor position in this file (the "..." menu and its Lifecycle/Assign
// submenus) is now captured from the CLICK EVENT's own coordinates
// (e.clientX/e.clientY), not from measuring the clicked element's DOM layout
// (getBoundingClientRect). That element-measuring approach was the actual
// root cause of the "menu glitches to top-left" bug, not just a symptom to
// guard around — this list is virtualized (react-window), and a click can
// land on a real, correctly-sized button whose ROW hasn't been positioned by
// react-window yet, or is mid-transition through a loading skeleton, giving
// a layout read that's technically valid-looking but wrong. A mouse event's
// own coordinates have no such dependency — they're just where the click
// happened, always accurate (Rahim, 2026-08-21: "you have been trying to fix
// it for a while now" — this removes the dependency instead of patching
// around another variant of it).

// The Lifecycle/Assign submenus read a step bigger than their own parent "..."
// menu — a nested panel opening off a smaller one looked cramped by comparison
// (Rahim, 2026-08-19: "a little just a little larger than the parent one").
const SUBMENU_ITEM_SX = { ...REPORTS_MENU_ITEM_SX, fontSize: "0.925rem", py: 1.05, px: 1.75 };
const SUBMENU_PAPER_SX = { ...MENU_PAPER_SX, borderRadius: "18px" };

// Shared skeleton row for the Lifecycle/Assign submenus' loading state — same
// icon+text row shape as the real rows (matched to REPORTS_MENU_ITEM_SX's own
// spacing so there's no layout jump once real data replaces it), replacing the
// plain spinner per Rahim's ask (2026-08-19).
const SubmenuRowSkeleton = () => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 1.75, py: 1.05, mx: 0.5, mb: 0.25 }}>
    <Skeleton variant="circular" width={26} height={26} />
    <Skeleton variant="text" width="60%" height={19} />
  </Box>
);

const formatTimestamp = (timestamp) => formatMessageTime(timestamp);

const transformMessages = (messages) => {
  const messageMap = new Map();
  messages.forEach((originalMessage) => {
    if (!originalMessage) return;
    const message = { ...originalMessage };
    if (!message.contactId) return;

    const { contactId } = message;
    const existingMessage = messageMap.get(contactId);
    const currentTimestamp = message.lastMessage?.timestamp;
    const existingTimestamp = existingMessage?.lastMessage?.timestamp;

    if (
      !existingMessage ||
      (currentTimestamp && existingTimestamp &&
       new Date(currentTimestamp) > new Date(existingTimestamp))
    ) {
      messageMap.set(contactId, message);
    }
  });

  return Array.from(messageMap.values()).sort((a, b) => {
    const aPinned = a.pinned || false;
    const bPinned = b.pinned || false;
    if (aPinned && !bPinned) return -1;
    if (!aPinned && bPinned) return 1;
    if (aPinned && bPinned) {
      const pinnedAtA = a.pinnedAt ? new Date(a.pinnedAt) : new Date(0);
      const pinnedAtB = b.pinnedAt ? new Date(b.pinnedAt) : new Date(0);
      if (pinnedAtB - pinnedAtA !== 0) return pinnedAtB - pinnedAtA;
    }
    const timestampA = a.lastMessage?.timestamp;
    const timestampB = b.lastMessage?.timestamp;
    if (!timestampA && !timestampB) return 0;
    if (!timestampA) return 1;
    if (!timestampB) return -1;
    return new Date(timestampB) - new Date(timestampA);
  });
};

// Menu component with anchorPosition for virtualization compatibility
const MenuWithAnchor = ({ anchorPosition, menuOpenContactId, currentMenuMessage, onClose, onTogglePin, onDeleteChat, onDeleteContact, onBlockContact, onUnblockContact, onLifecycleChanged, isBlockedTab }) => {
  const dispatch = useDispatch();
  const handleMenuClose = React.useCallback((event, reason) => {
    if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
      onClose(event, reason);
    }
  }, [onClose]);

  const handleMenuItemClick = React.useCallback((action) => {
    return (e) => {
      e.stopPropagation();
      onClose(e, 'menuItemClick');
      action();
    };
  }, [onClose]);

  // Lifecycle submenu — reference-style "chevron opens a submenu" pattern (2026-08-19),
  // built directly here (not via LifecycleStageChip) since this menu is presentational
  // and anchor-position-based, a different shape than that component's own click-to-open
  // Menu. Same underlying data/action as the inline chip: fetchLifecycleStages (guarded,
  // same cached-thunk dedup) + moveContactLifecycleStage, success patched back into the
  // Inbox list via onLifecycleChanged (MessageCard's already-existing handler).
  // anchorPosition (a captured {top,left} point), not anchorEl (a live DOM
  // ref) — same reason the outer "..." menu above already uses
  // anchorReference="anchorPosition": this list is virtualized, and picking
  // a stage/assignee here changes the exact row data these MenuItems
  // re-render from, which could invalidate a DOM-ref anchor mid-flow and
  // glitch the submenu to (0,0)/top-left, same bug already fixed for the
  // top navbar's own dropdown (Rahim, 2026-08-21 audit).
  const [lifecyclePosition, setLifecyclePosition] = React.useState(null);
  const stages = useSelector((s) => s.lifecycleStage.stages);
  const roleName = useSelector((s) => s.userDetails?.userDetails?.roleName || null);
  const LifecyclePermissions = useSelector((s) => s.userDetails?.userPermissions?.permissions?.Lifecycle || {});
  const canEditLifecycle = roleName === "Owner" || LifecyclePermissions.Edit;

  React.useEffect(() => {
    if (!stages.length) dispatch(fetchLifecycleStages());
  }, [dispatch, stages.length]);

  const handleLifecycleItemClick = React.useCallback((e) => {
    e.stopPropagation();
    // Click's own coordinates, not the row's layout — see handleMenuOpen.
    setLifecyclePosition({ top: e.clientY, left: e.clientX });
  }, []);

  const handleLifecycleSubmenuClose = React.useCallback((e) => {
    e?.stopPropagation?.();
    setLifecyclePosition(null);
  }, []);

  const handleStageSelect = React.useCallback(async (e, toStageId) => {
    e.stopPropagation();
    setLifecyclePosition(null);
    onClose(e, 'menuItemClick');
    if (!toStageId || !currentMenuMessage || toStageId === currentMenuMessage.lifecycleStageId) return;
    try {
      await dispatch(moveContactLifecycleStage({ contactId: currentMenuMessage.contactId, toStageId })).unwrap();
      onLifecycleChanged?.(currentMenuMessage.contactId, toStageId);
    } catch (err) {
      console.error("Failed to move Lifecycle stage:", err);
    }
  }, [dispatch, currentMenuMessage, onClose, onLifecycleChanged]);

  // Assign submenu (2026-08-19) — reuses the SAME data/action AssignBox.jsx already
  // uses (fetchInboxTeamData, assignContact, inboxSettingsSlice's teams/teamMembers),
  // just a compact list instead of that component's big-dialog layout. Only the
  // "assign to someone" half is built here — the reference's "Unassigned" (remove
  // assignment) option is skipped, per Rahim's explicit call: no backend support for
  // that exists yet (confirmed via audit), separate task.
  // anchorPosition, not anchorEl — same reason as lifecyclePosition above
  // (2026-08-21 glitch fix).
  const [assignPosition, setAssignPosition] = React.useState(null);
  const [assignSearchQuery, setAssignSearchQuery] = React.useState("");
  const [assigningId, setAssigningId] = React.useState(null);
  const teams = useSelector((s) => (Array.isArray(s.inboxSettings.teams) ? s.inboxSettings.teams : []));
  const teamMembers = useSelector((s) => (Array.isArray(s.inboxSettings.teamMembers) ? s.inboxSettings.teamMembers : []));
  const teamDataLoading = useSelector((s) => s.inboxSettings.teamDataLoading);
  const TeamPermissions = useSelector((s) => s.userDetails.userPermissions?.permissions?.Team || {});
  const TeamMemberPermissions = useSelector((s) => s.userDetails.userPermissions?.permissions?.TeamMember || {});
  const ConversationPermissions = useSelector((s) => s.userDetails.userPermissions?.permissions?.Conversation || {});
  const canAssignToTeam = ConversationPermissions.EditAll || ConversationPermissions.EditAssignedToTeam;
  const canAssignToMember = ConversationPermissions.EditAll || ConversationPermissions.EditAssignedToTeamMembers;
  const teamDataFetchedRef = React.useRef(false);

  const handleAssignItemClick = React.useCallback((e) => {
    e.stopPropagation();
    // Click's own coordinates, not the row's layout — see handleMenuOpen.
    setAssignPosition({ top: e.clientY, left: e.clientX });
    // Lazy fetch — only when Assign is actually opened, not on every "..." menu
    // mount. fetchInboxTeamData already has its own debounce/cache built in
    // (inboxSettingsSlice.js), this ref just avoids re-dispatching on every reopen
    // within the same mounted instance.
    if (!teamDataFetchedRef.current) {
      teamDataFetchedRef.current = true;
      dispatch(fetchInboxTeamData());
    }
  }, [dispatch]);

  const handleAssignSubmenuClose = React.useCallback((e) => {
    e?.stopPropagation?.();
    setAssignPosition(null);
    setAssignSearchQuery("");
  }, []);

  const handleAssignSelect = React.useCallback(async (e, id, type, name) => {
    e.stopPropagation();
    if (assigningId) return;
    if (type === "team" && !canAssignToTeam) return;
    if (type === "teammember" && !canAssignToMember) return;
    setAssigningId(id);
    try {
      await dispatch(assignContact({ Id: id, contactId: currentMenuMessage.contactId, type, Name: name })).unwrap();
      // Instant local feedback — the real live-sync path is the socket-driven
      // `assignmentUpdate` window event (fired server-side, already listened for
      // elsewhere in this file), but that round-trip has a moment of lag. This is
      // the exact same reducer that event already dispatches, just applied
      // immediately from the action that just succeeded.
      dispatch(updateAssignmentInInbox({
        contactId: currentMenuMessage.contactId,
        assignedTo: id,
        assignedToName: name,
        assignedToId: id,
        status: "Assigned",
      }));
      setAssignPosition(null);
      setAssignSearchQuery("");
      onClose(e, 'menuItemClick');
    } catch (err) {
      console.error("Failed to assign contact:", err);
    } finally {
      setAssigningId(null);
    }
  }, [dispatch, currentMenuMessage, assigningId, canAssignToTeam, canAssignToMember, onClose]);

  // "Assign to AI" submenu — same functionality as the "Assign to AI" button in
  // ChatboxNavbar.jsx (same enable endpoint, same agent picker, same one-way rule:
  // hides once AI already owns this contact), just built as a click-to-open submenu
  // here so the "..." menu's own UI/interaction pattern stays consistent with its
  // Lifecycle/Assign submenus above, instead of a separate Popover-shaped component.
  const [aiAssignPosition, setAiAssignPosition] = React.useState(null);
  const [assigningAiAgentId, setAssigningAiAgentId] = React.useState(null);
  const aiAgents = useSelector((s) => s.aiAgents?.agents || []);
  const aiAgentsLoading = useSelector((s) => s.aiAgents?.loading || false);
  const assignToAiLoading = useSelector((s) => s.aiAssistStatus?.assignToAiLoading);
  const workspaceId = useSelector((s) => s.workspace?.currentWorkspace?._id);
  // Gates isBlockWindowExpired below -- Meta's 24h reply-window rule doesn't apply to a
  // QR-connected workspace (same isQrConnectedWorkspace as TextBox.jsx/MessageItem below,
  // a separate component so its own copy is needed here).
  const isQrConnectedWorkspace = useSelector((s) => s.workspace?.currentWorkspace?.whatsappConnectionType === "baileys");
  const aiAgentsFetchedRef = React.useRef(false);
  // Only agents the workspace can actually hand a conversation to — mirrors the
  // isActive filter AiAssignBox.jsx (ChatboxNavbar's own picker) already applies.
  const activeAiAgents = React.useMemo(
    () => aiAgents.filter((agent) => agent.isActive !== false),
    [aiAgents]
  );

  const handleAiAssignItemClick = React.useCallback((e) => {
    e.stopPropagation();
    setAiAssignPosition({ top: e.clientY, left: e.clientX });
    // Lazy fetch, same guard style as teamDataFetchedRef above — only when this
    // submenu is actually opened, not on every "..." menu mount. fetchAiAgents is
    // a cached thunk (aiAgentsSlice.js), so this is a no-op if InboxAll.jsx/
    // InboxSettings.jsx already fetched it for this workspace.
    if (!aiAgentsFetchedRef.current && workspaceId) {
      aiAgentsFetchedRef.current = true;
      dispatch(fetchAiAgents({ workspaceId }));
    }
  }, [dispatch, workspaceId]);

  const handleAiAssignSubmenuClose = React.useCallback((e) => {
    e?.stopPropagation?.();
    setAiAssignPosition(null);
  }, []);

  const handleAiAssignSelect = React.useCallback(async (e, agent) => {
    e.stopPropagation();
    const contactID = currentMenuMessage?.contactId;
    if (!contactID || !workspaceId || !agent?._id || assigningAiAgentId || assignToAiLoading) return;
    const { blocked, message: blockMsg } = await checkBeforeOutbound(getAuthToken, []);
    if (blocked) {
      toast.error(blockMsg, { id: 'subscription-blocked', duration: 5000 });
      return;
    }
    setAssigningAiAgentId(agent._id);
    dispatch(setAssignToAiLoading(true)); // drives TextBox.jsx's "Pondering..." composer loader too, if this contact is open
    try {
      const token = await getAuthToken();
      const SERVER_DOMAIN = process.env.REACT_APP_SERVER_DOMAIN || "http://localhost:8000";
      await axios.post(
        `${SERVER_DOMAIN}/api/workspaces/${workspaceId}/ai/inbox-assist/takeover/${contactID}/enable`,
        { agentId: agent._id },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`${agent.name || "AI"} is now handling this conversation`);
      invalidateCachedContact(contactID);
      // Instant local feedback — same "socket round-trip still coming, but don't
      // wait for it" pattern handleAssignSelect above already uses for human
      // assignment. assignedToIsAi is what lets the card show "(AI)" instead of
      // reading like a same-named human/team assignee (see MessageItem below).
      dispatch(updateAssignmentInInbox({
        contactId: contactID,
        assignedTo: agent._id,
        assignedToName: agent.name,
        assignedToId: agent._id,
        assignedToIsAi: true,
        status: "Assigned",
      }));
      setAiAssignPosition(null);
      onClose(e, 'menuItemClick');
    } catch (err) {
      console.error("Failed to enable AI Takeover:", err);
      toast.error(err.response?.data?.message || "Failed to assign this conversation to AI");
    } finally {
      dispatch(setAssignToAiLoading(false));
      setAssigningAiAgentId(null);
    }
  }, [dispatch, currentMenuMessage, workspaceId, assigningAiAgentId, assignToAiLoading, onClose]);

  const assignSearchLower = assignSearchQuery.toLowerCase();
  const filteredTeams = teams.filter((t) => t.teamName?.toLowerCase().includes(assignSearchLower));
  const filteredTeamMembers = teamMembers.filter((m) => m.name?.toLowerCase().includes(assignSearchLower));

  if (!menuOpenContactId || !currentMenuMessage || !anchorPosition) return null;

  // Shared between both branches below (blocked-tab / normal) — Lifecycle applies
  // regardless of block status, only one branch is ever mounted at a time.
  const lifecycleMenuItem = (
    <MenuItem
      onClick={handleLifecycleItemClick}
      dense
      sx={{ ...REPORTS_MENU_ITEM_SX, display: "flex", alignItems: "center", justifyContent: "space-between" }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Box sx={MENU_ICON_BOX_SX}><LifecycleIcon size="100%" color="currentColor" /></Box>
        Lifecycle
      </Box>
      <Box sx={{ width: 14, height: 14, display: "flex", alignItems: "center", justifyContent: "center", color: "text.secondary" }}>
        <ArrowRight2 size="100%" color="currentColor" />
      </Box>
    </MenuItem>
  );

  const assignMenuItem = (
    <MenuItem
      onClick={handleAssignItemClick}
      dense
      sx={{ ...REPORTS_MENU_ITEM_SX, display: "flex", alignItems: "center", justifyContent: "space-between" }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Box sx={MENU_ICON_BOX_SX}><AssignIcon size="100%" color="currentColor" /></Box>
        Assign
      </Box>
      <Box sx={{ width: 14, height: 14, display: "flex", alignItems: "center", justifyContent: "center", color: "text.secondary" }}>
        <ArrowRight2 size="100%" color="currentColor" />
      </Box>
    </MenuItem>
  );

  // One-way only, same rule as the ChatboxNavbar button — hides once AI already
  // owns this contact (Take Back stays a composer-only control, not here).
  const aiAssignMenuItem = !currentMenuMessage.assignedToIsAi ? (
    <MenuItem
      onClick={handleAiAssignItemClick}
      dense
      sx={{ ...REPORTS_MENU_ITEM_SX, display: "flex", alignItems: "center", justifyContent: "space-between" }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Box sx={MENU_ICON_BOX_SX}><AiUsersIcon width="100%" height="100%" /></Box>
        Assign to AI
      </Box>
      <Box sx={{ width: 14, height: 14, display: "flex", alignItems: "center", justifyContent: "center", color: "text.secondary" }}>
        <ArrowRight2 size="100%" color="currentColor" />
      </Box>
    </MenuItem>
  ) : null;

  const isValidPosition =
    typeof anchorPosition.top === 'number' &&
    typeof anchorPosition.left === 'number' &&
    !isNaN(anchorPosition.top) &&
    !isNaN(anchorPosition.left);

  if (!isValidPosition) return null;

  // WhatsApp only: block_users requires the contact to have messaged within 24h (see
  // blockContact.service.js). activeStatus is "hours remaining in the 24h window" already
  // delivered on every row (0 once expired) — reuse it here to gray out Block up front
  // instead of only rejecting the click (handleBlockContact still guards this too).
  // Doesn't apply to a QR-connected workspace -- see blockContact.service.js's own
  // resolveProviderForWorkspace skip, matching isQrConnectedWorkspace above.
  const isWhatsAppMenuContact = (currentMenuMessage.platform || 'whatsapp') === 'whatsapp';
  const isBlockWindowExpired = isWhatsAppMenuContact && !isQrConnectedWorkspace && !isQrChat(currentMenuMessage) && !(currentMenuMessage.activeStatus > 0);

  return (
    <>
    <Menu
      anchorReference="anchorPosition"
      anchorPosition={anchorPosition}
      open={Boolean(menuOpenContactId)}
      onClose={handleMenuClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      transformOrigin={{ vertical: "top", horizontal: "right" }}
      disableScrollLock
      // Delete Contact/Chat/Block open the confirm Dialog the instant this
      // Menu closes (handleMenuItemClick calls onClose then the action in
      // the same tick) — with the Menu's default ~200ms fade-out transition,
      // its backdrop was still visible while the Dialog's own backdrop faded
      // in on top, stacking to read as darker than the Create Contact
      // dialog's single backdrop (Rahim, 2026-08-31: "the background overlay
      // is too dark for the delete contact one"). Instant close removes the
      // overlap.
      transitionDuration={0}
      slotProps={{
        paper: {
          sx: {
            ...MENU_PAPER_SX,
            mt: 0.5,
            minWidth: 210,
            zIndex: 1300,
          },
          onClick: (e) => e.stopPropagation(),
          onMouseDown: (e) => e.stopPropagation(),
        }
      }}
      MenuListProps={{
        'aria-labelledby': 'chat-actions-menu',
        onClick: (e) => e.stopPropagation(),
        onMouseDown: (e) => e.stopPropagation(),
      }}
      disableAutoFocusItem
      disableRestoreFocus
    >
      {isBlockedTab ? (
        <>
          <MenuItem
            onClick={handleMenuItemClick(() => onUnblockContact(currentMenuMessage.contactId))}
            dense
            sx={REPORTS_MENU_ITEM_SX}
          >
            <Box sx={MENU_ICON_BOX_SX}><UnlockIcon size="100%" color="currentColor" /></Box>
            Unblock
          </MenuItem>
          {assignMenuItem}
          {aiAssignMenuItem}
          {lifecycleMenuItem}
          <MenuItem
            onClick={handleMenuItemClick(() => onDeleteContact(currentMenuMessage.contactId))}
            dense
            sx={{ ...REPORTS_MENU_ITEM_SX, color: (theme) => theme.palette.error.main }}
          >
            <Box sx={MENU_ICON_BOX_SX}><UserRemoveIcon size="100%" color="currentColor" /></Box>
            Delete Contact
          </MenuItem>
        </>
      ) : currentMenuMessage.isGroup ? (
        // A group has no assignment/AI-takeover/lifecycle/block/contact-delete concept
        // (WhatsApp Groups plan decision #2) -- only Pin and Delete Chat carry over, both
        // reused completely unchanged (they map onto fields already on GroupModel/
        // GroupConversationModel).
        <>
          <MenuItem
            onClick={handleMenuItemClick(() => onTogglePin(currentMenuMessage.contactId))}
            dense
            sx={REPORTS_MENU_ITEM_SX}
          >
            <PushPinOutlinedIcon sx={{ fontSize: "18px", color: "inherit" }} />
            {currentMenuMessage.pinned ? "Unpin" : "Pin"}
          </MenuItem>
          <MenuItem
            onClick={handleMenuItemClick(() => onDeleteChat(currentMenuMessage.contactId))}
            dense
            sx={REPORTS_MENU_ITEM_SX}
          >
            <Box sx={MENU_ICON_BOX_SX}><TrashIcon size="100%" color="currentColor" /></Box>
            Delete Chat
          </MenuItem>
        </>
      ) : (
        <>
          <MenuItem
            onClick={handleMenuItemClick(() => onTogglePin(currentMenuMessage.contactId))}
            dense
            sx={REPORTS_MENU_ITEM_SX}
          >
            <PushPinOutlinedIcon sx={{ fontSize: "18px", color: "inherit" }} />
            {currentMenuMessage.pinned ? "Unpin" : "Pin"}
          </MenuItem>
          {assignMenuItem}
          {aiAssignMenuItem}
          {lifecycleMenuItem}
          <MenuItem
            onClick={handleMenuItemClick(() => onDeleteChat(currentMenuMessage.contactId))}
            dense
            sx={REPORTS_MENU_ITEM_SX}
          >
            <Box sx={MENU_ICON_BOX_SX}><TrashIcon size="100%" color="currentColor" /></Box>
            Delete Chat
          </MenuItem>
          <MenuItem
            onClick={handleMenuItemClick(() => onDeleteContact(currentMenuMessage.contactId))}
            dense
            sx={{ ...REPORTS_MENU_ITEM_SX, color: (theme) => theme.palette.error.main }}
          >
            <Box sx={MENU_ICON_BOX_SX}><UserRemoveIcon size="100%" color="currentColor" /></Box>
            Delete Contact
          </MenuItem>
          <Tooltip
            title={isBlockWindowExpired ? "Meta does not allow blocking a WhatsApp contact outside the 24-hour messaging window." : ""}
            placement="left"
            arrow
            slotProps={{
              tooltip: {
                sx: {
                  ...DARK_BUBBLE_TOOLTIP_SX.tooltip.sx,
                  fontSize: "0.85rem",
                  lineHeight: 1.4,
                  maxWidth: 260,
                  padding: "8px 12px",
                },
              },
              arrow: DARK_BUBBLE_TOOLTIP_SX.arrow,
            }}
          >
            {/* span wrapper: MUI disabled elements don't fire pointer events, so the
                Tooltip needs a non-disabled parent to hover on */}
            <span>
              <MenuItem
                onClick={
                  isBlockWindowExpired
                    ? undefined
                    : handleMenuItemClick(() => onBlockContact(currentMenuMessage.contactId))
                }
                disabled={isBlockWindowExpired}
                dense
                sx={{
                  ...REPORTS_MENU_ITEM_SX,
                  color: (theme) => (isBlockWindowExpired ? theme.palette.text.disabled : theme.palette.error.main),
                }}
              >
                <BlockIcon sx={{ fontSize: "18px", color: "inherit" }} />
                Block
              </MenuItem>
            </span>
          </Tooltip>
        </>
      )}
    </Menu>

    {/* Lifecycle submenu — separate Menu (not nested; MUI Menus portal independently),
        anchored to a captured {top,left} point (see lifecyclePosition above), not
        the "Lifecycle" item's own DOM node — same anchorPosition approach the outer
        "..." menu already uses, for the same virtualized-list reason (2026-08-21
        glitch fix). Click-to-open per Rahim's explicit ask (2026-08-19) not hover —
        no hover-opens-submenu precedent exists anywhere else in this app. */}
    <Menu
      anchorReference="anchorPosition"
      anchorPosition={lifecyclePosition || undefined}
      open={Boolean(lifecyclePosition)}
      onClose={handleLifecycleSubmenuClose}
      anchorOrigin={{ vertical: "top", horizontal: "right" }}
      transformOrigin={{ vertical: "top", horizontal: "left" }}
      slotProps={{
        paper: {
          // Fixed width — without this the paper shrinks to its narrowest content
          // (just the "Lifecycle Stages" header), reading as a cramped sliver
          // instead of a proper panel like the reference (Rahim, 2026-08-19).
          sx: { minWidth: 245, ...SUBMENU_PAPER_SX },
          onClick: (e) => e.stopPropagation(),
          onMouseDown: (e) => e.stopPropagation(),
        },
      }}
    >
      {!stages.length ? (
        <Box sx={{ p: 0.75 }}>
          <SubmenuRowSkeleton />
          <SubmenuRowSkeleton />
          <SubmenuRowSkeleton />
        </Box>
      ) : (
        <LifecycleStageMenuList
          stages={stages}
          currentStageId={currentMenuMessage?.lifecycleStageId}
          onSelect={handleStageSelect}
          itemSx={SUBMENU_ITEM_SX}
          selectedSx={reportsDropdownSelectedSx}
          disabled={!canEditLifecycle}
        />
      )}
    </Menu>

    {/* Assign submenu — search + Teams/Team Members, same data AssignBox.jsx already
        uses. Anchored to a captured point (assignPosition), not a DOM ref — same
        virtualized-list glitch fix as the Lifecycle submenu above (2026-08-21).
        Click-to-open like Lifecycle, same reasons (no hover-submenu precedent
        in this app). "Unassigned" (remove assignment) intentionally omitted — no
        backend support exists for it yet, confirmed via audit, separate task. */}
    <Menu
      anchorReference="anchorPosition"
      anchorPosition={assignPosition || undefined}
      open={Boolean(assignPosition)}
      onClose={handleAssignSubmenuClose}
      anchorOrigin={{ vertical: "top", horizontal: "right" }}
      transformOrigin={{ vertical: "top", horizontal: "left" }}
      slotProps={{
        paper: {
          sx: { minWidth: 270, maxHeight: 380, overflowY: "auto", ...SUBMENU_PAPER_SX },
          onClick: (e) => e.stopPropagation(),
          onMouseDown: (e) => e.stopPropagation(),
        },
      }}
    >
      <Box sx={{ p: 0.75 }}>
        <Box
          onClick={(e) => e.stopPropagation()}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.75,
            position: "sticky",
            top: 0,
            zIndex: 1,
            backgroundColor: (theme) => theme.palette.background.paper,
            mb: 1,
            px: 1.25,
            py: 0.625,
            borderRadius: "8px",
            border: (theme) => `1px solid ${theme.palette.divider}`,
          }}
        >
          <Box sx={{ width: 17, height: 17, display: "flex", alignItems: "center", justifyContent: "center", color: "text.secondary", flexShrink: 0 }}>
            <SearchIcon size="100%" color="currentColor" />
          </Box>
          <InputBase
            autoFocus
            placeholder="Search teams and members"
            value={assignSearchQuery}
            onChange={(e) => setAssignSearchQuery(e.target.value)}
            onKeyDown={(e) => e.stopPropagation()}
            sx={{ fontSize: "0.925rem", flex: 1, color: "text.primary" }}
          />
        </Box>

        {teamDataLoading ? (
          <>
            <SubmenuRowSkeleton />
            <SubmenuRowSkeleton />
            <SubmenuRowSkeleton />
            <SubmenuRowSkeleton />
          </>
        ) : (
          <>
            {TeamPermissions.View && (
              <>
                <Typography sx={{ pl: 1.25, pr: 1, pt: 1, pb: 0.5, fontSize: "0.7rem", fontWeight: 600, color: "text.secondary" }}>
                  Teams
                </Typography>
                {filteredTeams.length === 0 ? (
                  <Typography sx={{ pl: 1.25, pb: 1, fontSize: "0.75rem", color: "text.secondary", fontStyle: "italic" }}>
                    No teams found
                  </Typography>
                ) : (
                  filteredTeams.map((team) => (
                    <MenuItem
                      key={team.id}
                      disabled={!canAssignToTeam || assigningId === team.id}
                      onClick={(e) => handleAssignSelect(e, team.id, "team", team.teamName)}
                      dense
                      sx={{ ...SUBMENU_ITEM_SX, mx: 0, pl: 1.25 }}
                    >
                      <Avatar sx={{ width: 26, height: 26, fontSize: "0.8125rem", bgcolor: (theme) => theme.palette.primary.main }}>
                        {team.teamName?.[0]}
                      </Avatar>
                      {team.teamName}
                      {assigningId === team.id && <CircularProgress size={12} sx={{ ml: "auto" }} />}
                    </MenuItem>
                  ))
                )}
              </>
            )}

            {TeamMemberPermissions.View && (
              <>
                <Typography sx={{ pl: 1.25, pr: 1, pt: 1, pb: 0.5, fontSize: "0.7rem", fontWeight: 600, color: "text.secondary" }}>
                  Team Members
                </Typography>
                {filteredTeamMembers.length === 0 ? (
                  <Typography sx={{ pl: 1.25, pb: 1, fontSize: "0.75rem", color: "text.secondary", fontStyle: "italic" }}>
                    No team members found
                  </Typography>
                ) : (
                  filteredTeamMembers.map((member) => (
                    <MenuItem
                      key={member.id}
                      disabled={!canAssignToMember || assigningId === member.id}
                      onClick={(e) => handleAssignSelect(e, member.id, "teammember", member.name)}
                      dense
                      sx={{ ...SUBMENU_ITEM_SX, mx: 0, pl: 1.25 }}
                    >
                      <Avatar sx={{ width: 26, height: 26, fontSize: "0.8125rem", bgcolor: (theme) => theme.palette.secondary.main }}>
                        {member.name?.[0]}
                      </Avatar>
                      {member.name}
                      {assigningId === member.id && <CircularProgress size={12} sx={{ ml: "auto" }} />}
                    </MenuItem>
                  ))
                )}
              </>
            )}
          </>
        )}
      </Box>
    </Menu>

    {/* Assign to AI submenu — same click-to-open, anchorPosition pattern as the
        Lifecycle/Assign submenus above, and the same enable endpoint/agent picker
        ChatboxNavbar.jsx's own "Assign to AI" button uses (AiAssignBox.jsx there;
        this is the same functionality, just shaped as a submenu to match this
        menu's own UI instead of a separate Popover component). */}
    <Menu
      anchorReference="anchorPosition"
      anchorPosition={aiAssignPosition || undefined}
      open={Boolean(aiAssignPosition)}
      onClose={handleAiAssignSubmenuClose}
      anchorOrigin={{ vertical: "top", horizontal: "right" }}
      transformOrigin={{ vertical: "top", horizontal: "left" }}
      slotProps={{
        paper: {
          sx: { minWidth: 240, maxHeight: 320, overflowY: "auto", ...SUBMENU_PAPER_SX },
          onClick: (e) => e.stopPropagation(),
          onMouseDown: (e) => e.stopPropagation(),
        },
      }}
    >
      <Box sx={{ p: 0.75 }}>
        {aiAgentsLoading && activeAiAgents.length === 0 ? (
          <>
            <SubmenuRowSkeleton />
            <SubmenuRowSkeleton />
          </>
        ) : activeAiAgents.length === 0 ? (
          <Typography sx={{ pl: 1.25, pr: 1, pt: 0.5, pb: 1, fontSize: "0.75rem", color: "text.secondary", fontStyle: "italic" }}>
            No active AI agents configured
          </Typography>
        ) : (
          activeAiAgents.map((agent) => (
            <MenuItem
              key={agent._id}
              disabled={assigningAiAgentId === agent._id}
              onClick={(e) => handleAiAssignSelect(e, agent)}
              dense
              sx={{ ...SUBMENU_ITEM_SX, mx: 0, pl: 1.25 }}
            >
              <Avatar sx={{ width: 26, height: 26, fontSize: "0.8125rem", bgcolor: (theme) => stringToColor(agent.name || "AI Agent") }}>
                {(agent.name || "A")[0]}
              </Avatar>
              {agent.name || "AI Agent"}
              {assigningAiAgentId === agent._id && <CircularProgress size={12} sx={{ ml: "auto" }} />}
            </MenuItem>
          ))
        )}
      </Box>
    </Menu>
    </>
  );
};

/** Short label for Recent list Assigned chip: first word if spaced; single token → max 15 chars. */
// People show their first name; teams show their whole name (a team's first word is often
// just a prefix like "01-", e.g. "01- GDS Ticketing"). The chip's own maxWidth + ellipsis
// cuts anything too long; the tooltip always has the full name.
const formatAssigneeChipLabel = (raw, isTeam = false) => {
  const trimmed = (raw || "").trim();
  if (!trimmed) return "";
  if (isTeam) return trimmed;
  const parts = trimmed.split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "";
  if (parts.length === 1) {
    const w = parts[0];
    return w.length > 15 ? `${w.slice(0, 15)}…` : w;
  }
  return parts[0];
};

// Derives the last-message preview text. Extracted to avoid a 125-line IIFE in JSX per render.
const LOG_ONLY_MESSAGE_TYPES = new Set(["assignment", "date", "closechat", "blockcontact"]);

function getMessagePreview(message) {
  if (!message.lastMessage) return "";
  // Log rows (assignment, chat-closed, date separators) never carry a real
  // sent/received message — only their own type/body, never real `content`.
  // If no genuine content survived, show nothing rather than the log text.
  if (LOG_ONLY_MESSAGE_TYPES.has(message.lastMessage.type) && !message.lastMessage.content) {
    return "";
  }
  // Inbox-only layer: a plain text message whose real sender is a workflow/
  // broadcast/partner-API actor previews as that sender's name instead of
  // the message body itself. Everything else (including every non-text
  // type's label and the JSON-sniffing for location/contact/product) is the
  // shared formatter below — see formatLastMessagePreview.js's own comment
  // for why this moved out to a shared util (2026-09-09: the Dashboard's
  // ContactRow.jsx had no equivalent for any of this and was previewing raw
  // JSON/URLs for these same message types).
  const messageType = classifyLastMessageType(message.lastMessage);
  if (messageType === "text") {
    const resolvedSender = resolveDisplaySender({
      performedBy: message.lastMessage.performedBy,
      performedByName: message.lastMessage.performedByName,
      senderName: message.lastMessage.sendername,
      isWorkflowAutomated: message.lastMessage.isWorkflowAutomated === true,
      isBulkMessage: message.lastMessage.isBulkMessage === true,
    });
    if (resolvedSender.isWorkflowAutomated || resolvedSender.sendername === "Broadcast" || resolvedSender.sendername === "Partner API") {
      return resolvedSender.sendername || "Automated";
    }
  }
  return formatLastMessagePreview(message.lastMessage);
}

// Individual Message Item Component for virtualization.
// Does NOT receive menuOpenContactId as a prop — reads it from context instead.
// This means only the row whose contactId matches will re-render on menu open/close.
const MessageItem = React.memo(({ message, index, style, isSelected, onMessageClick, onMenuOpen, onLifecycleChanged, bulkSelectable, assigneeIsTeam }) => {
  const theme = useTheme();
  // Inbox multi-select (see inboxBulkSelectionSlice.js). Per-row selectors so ticking one row
  // only re-renders that row, plus one boolean for "selection mode on" (flips only when the
  // first box is ticked / the last one cleared). Group chats aren't contacts — never selectable.
  const dispatch = useDispatch();
  const reduxStore = useStore();
  const canBulkSelect = !!bulkSelectable && !message.isGroup;
  const isBulkChecked = useSelector(selectIsBulkSelected(message.contactId));
  const bulkSelectionActive = useSelector(selectBulkSelectionActive);
  // Hovering the avatar itself (not the whole row) swaps it for the checkbox, so moving the
  // cursor down the list doesn't flicker every avatar it passes.
  const [avatarHovered, setAvatarHovered] = React.useState(false);
  const showBulkCheckbox = canBulkSelect && (avatarHovered || bulkSelectionActive || isBulkChecked);
  const handleBulkToggle = React.useCallback((event) => {
    event.stopPropagation();
    event.preventDefault();
    if (!isBulkChecked && selectBulkSelectionCount(reduxStore.getState()) >= MAX_BULK_SELECTION) {
      toast.error(`You can select up to ${MAX_BULK_SELECTION} conversations at a time`, { id: "inbox-bulk-max" });
      return;
    }
    dispatch(toggleBulkSelection(message.contactId));
  }, [dispatch, reduxStore, isBulkChecked, message.contactId]);
  // Own copy of the same selector MenuWithAnchor uses above (separate component, separate
  // scope) -- gates the 24h "activeStatus" pill/tooltip below, Meta's reply-window rule,
  // which doesn't apply to a QR-connected workspace. A QR chat in a workspace that also has a Meta number is
  // recognised by its own platform.
  const isQrOnlyWorkspace = useSelector((s) => s.workspace?.currentWorkspace?.whatsappConnectionType === "baileys");
  // A card that carries a QR number is a QR chat even if a live event left its platform unset.
  const isQrConnectedWorkspace = isQrOnlyWorkspace || isQrChat(message);
  // Read from context — only this component re-renders when its own menuOpen state changes
  const menuOpenContactId = React.useContext(MenuOpenContext);
  const menuOpen = menuOpenContactId === message.contactId;
  // JS-computed hover, not CSS `:hover` -- see HoveredRowContext's own
  // comment (top of file) for why: this virtualized, "Newest"-sorted list
  // can reorder a different contact into this exact screen slot without any
  // real mouse event firing, which is what let hover styling go stale
  // (Rahim, 2026-09-09: the long-deferred dropdown/chip hover glitch).
  // useSyncExternalStore with a per-row selector, not useContext directly --
  // this row only re-renders when ITS OWN isHovered boolean actually flips,
  // not on every hover-target change across the whole visible list (see the
  // store's own comment, top of file, for why plain Context was too slow).
  const hoverStore = React.useContext(HoveredRowContext);
  const isHovered = React.useSyncExternalStore(
    hoverStore.subscribe,
    () => hoverStore.getHoveredContactId() === message.contactId
  );

  const displayStatus = message.status || "Unassigned";
  const rawContactName =
    message.name != null && message.name !== ""
      ? String(message.name).trim()
      : "";
  // No longer a JS character-count cut (removed 2026-09-09) -- just trims.
  // Actual truncation is CSS-only now (text-overflow:ellipsis on the name
  // Typography below), so it tracks the row's real available width instead
  // of a fixed guess. See formatMessageCardContactDisplayName.js's own
  // comment for the full history.
  const contactNameLabel = formatMessageCardContactDisplayName(message.name);
  // Real overflow check (nameRef below), not a string-length heuristic --
  // shows the full-name tooltip only when the name is genuinely visually
  // truncated by CSS, at whatever width the row currently has.
  const nameRef = React.useRef(null);
  const [showContactNameTooltip, setShowContactNameTooltip] = React.useState(false);
  React.useEffect(() => {
    const el = nameRef.current;
    if (!el) return undefined;
    const checkTruncation = () => {
      // A transient 0x0 reading (e.g. a banner like "Reconnecting..."/"You are
      // away" toggling above the list mid-reflow, or any other layout churn
      // that briefly collapses this row) is never a real "not truncated"
      // state for an already-mounted row with real text -- it's the element
      // being measured mid-thrash. Treating it as a real measurement could
      // permanently latch showContactNameTooltip to false: if this 0x0 read
      // happens to be the LAST ResizeObserver callback before layout
      // stabilizes, no further callback ever fires to correct it, silently
      // disabling the tooltip for a genuinely truncated name until the row
      // unmounts/remounts (found live, 2026-09-09, by deliberately stress-
      // testing a live contact-name change against a reconnect-banner churn
      // loop -- Chrome's own dev socket server being down was what surfaced
      // it, but any layout shift with the same 0x0-then-settle timing could
      // trigger it in production too). Skip the update and wait for the next
      // real observation instead.
      if (el.clientWidth === 0 && el.scrollWidth === 0) return;
      setShowContactNameTooltip(el.scrollWidth > el.clientWidth);
    };
    checkTruncation();
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(checkTruncation);
    observer.observe(el);
    return () => observer.disconnect();
  }, [contactNameLabel]);

  const visibleUnreadCount = isSelected ? 0 : (message.receivedCount || 0);
  const unreadBadgeLabel = formatBadgeCount(visibleUnreadCount);

  const assigneeLabel = (message.assignedToName || message.assignedTo || "").trim();
  // AI Takeover hands the conversation to an AI agent rather than a team/team member
  // (see server/eventHandlers/aiTakeoverEventHandler.js) — flagged separately by the
  // backend since assignedToName alone can't be told apart from a same-named human.
  const assigneeIsAi = !!message.assignedToIsAi;
  const assigneeChipShort = formatAssigneeChipLabel(assigneeLabel, assigneeIsTeam && !assigneeIsAi) + (assigneeIsAi ? " (AI)" : "");
  const statusChipLabel =
    displayStatus === "Assigned" && assigneeChipShort.trim() ? assigneeChipShort : displayStatus;
  const statusChipTooltip =
    displayStatus === "Assigned" && assigneeLabel
      ? `${assigneeLabel}${assigneeIsAi ? " (AI)" : ""}`
      : displayStatus;

  // Memoize emoji to avoid repeated string hashing per render
  const avatarImage = React.useMemo(() => nameToContactAvatarImage(message.name), [message.name]);

  // Memoize the message preview text — track only the fields getMessagePreview reads
  const lmTimestamp   = message.lastMessage?.timestamp;
  const lmContent     = message.lastMessage?.content;
  const lmBody        = message.lastMessage?.body;
  const lmMessage     = message.lastMessage?.message;
  const lmType        = message.lastMessage?.type;
  const lmMessageType = message.lastMessage?.messageType;
  const messagePreview = React.useMemo(
    () => getMessagePreview(message),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [lmTimestamp, lmContent, lmBody, lmMessage, lmType, lmMessageType]
  );

  const handleMenuClick = React.useCallback((event) => {
    event.stopPropagation();
    event.preventDefault();
    // The click's own coordinates, not the target element's layout — see the
    // comment above handleMenuOpen for why (Rahim, 2026-08-21).
    onMenuOpen({ x: event.clientX, y: event.clientY }, message.contactId);
  }, [onMenuOpen, message.contactId]);

  const contactNameTypography = (
    <Typography
      ref={nameRef}
      variant="subtitle1"
      fontWeight="600"
      sx={{
        color: (theme) => theme.palette.text.primary,
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        // This Typography is itself a flex item (of the row Box below) — a
        // flex item's default min-width is its own content width, so without
        // this it could refuse to shrink small enough for the ellipsis above
        // to ever actually kick in, same root cause class as the fix above.
        minWidth: 0,
        // Matches the app's body-text token (same as the left nav's filter rows),
        // +1px per Rahim's ask (2026-08-20, applied across Chats+Calls card content).
        fontSize: "0.9375rem",
        lineHeight: 1.2,
        // NOT display:"flex" — text-overflow:ellipsis has no effect on flex
        // containers per spec (only block/inline-block), so that silently
        // disabled the ellipsis below and let the name hard-clip mid-word
        // straight into the timestamp next to it with no "…" (Rahim,
        // 2026-08-22: "saviza name and the date are colided"). The parent
        // row Box already centers this alongside the pin icon, so this
        // Typography doesn't need its own flex context.
        transition: "color 0.12s ease",
      }}
    >
      {contactNameLabel}
    </Typography>
  );

  return (
    <ListItem
      style={style}
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: { xs: "4px 6px", sm: "3px 5px", md: "5px 8px", lg: "6px 10px", xl: "7px 11px" },
        borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
        // Matches InboxSettings.jsx's own selected-row style (neutral grey, no
        // colored left bar) instead of the green tint + bar this used to have —
        // was inconsistent with the left nav's own selected-state convention.
        // Was a static value + a CSS "&:hover" override -- now isHovered-driven
        // (JS-computed, see HoveredRowContext's comment at the top of the file)
        // for the same reorder-under-a-stationary-cursor reason the arrow and
        // lifecycle chip above switched: a plain "&:hover" here would go stale
        // exactly the same way theirs did.
        backgroundColor: (theme) => {
          // Bulk-checked rows get a light brand tint, kept distinct from the neutral grey
          // "this chat is open" highlight below so the two never read as the same thing.
          if (isBulkChecked) {
            return alpha(theme.palette.primary.main, isHovered ? 0.14 : 0.09);
          }
          if (isHovered) {
            return isSelected
              ? theme.palette.mode === "dark" ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.09)"
              : theme.palette.mode === "dark" ? "rgba(255,255,255,0.055)" : "rgba(0,0,0,0.045)";
          }
          return isSelected
            ? theme.palette.mode === "dark" ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)"
            : "transparent";
        },
        // Bumped +20px (was 95/72px) — the restructured card (extra lifecycle-slot
        // row, right column now 3 stacked items) needs more real room now that
        // there's proper top/bottom padding; without this, rows with the unread-
        // count badge present overflowed and had their status badge clipped by
        // this row's own overflow:hidden.
        // Height itself back to 115/88 (Rahim, 2026-09-08: "make the height back to
        // what it was") -- the pt/pb reduction below stays, so within this same
        // height the top row (name) and bottom row (lifecycle chip/status badge,
        // already top/bottom-anchored via this column's justifyContent and the
        // other column's mt:"auto") sit closer to the row's edges, opening up
        // more space in the middle instead of shrinking the row.
        minHeight: { xs: "115px", sm: "88px", md: "115px", lg: "115px", xl: "115px" },
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Clickable Content Wrapper */}
      <Box
        onClick={(e) => {
          // In selection mode a click anywhere on the row ticks/unticks it instead of
          // opening the chat — the checkbox alone is too small a target.
          if (bulkSelectionActive && canBulkSelect) {
            handleBulkToggle(e);
            return;
          }
          if (!menuOpen && !e.target.closest('.delete-menu-container')) {
            onMessageClick(message);
          }
        }}
        sx={{
          display: "flex",
          flex: 1,
          // Explicit height — without this, the wrapper's own size came from its
          // content (name+preview+lifecycle-slot vs. timestamp+count+status), which
          // isn't constant: the unread-count badge appearing/disappearing changed the
          // wrapper's natural height by a few px. The row's alignItems:"center"
          // centers this wrapper within itself, so a shifting wrapper height shifted
          // the name up/down relative to the dropdown arrow (a separate, fixed-
          // position sibling that doesn't move) — confirmed live via getBoundingClientRect,
          // arrow sat 1.4px above the name with no count badge but 6.3px below it
          // with one. Locking height to 100% removes the variable entirely.
          height: "100%",
          // This app has no global box-sizing:border-box (a known recurring gotcha
          // here) — without this, height:100% + the pt/pb below ADD together under
          // content-box sizing instead of padding being eaten out of the 100%,
          // overflowing the row and getting silently clipped by its overflow:hidden.
          // That's why padding increases weren't visibly doing much before this.
          boxSizing: "border-box",
          // "stretch" (not "center") — the content column and the right column
          // (timestamp/count/status) need to start flush at the same top edge so
          // the name lines up with the timestamp. "center" was centering each
          // column as an independent block, so when their total heights differed
          // (e.g. after adding a row), their tops drifted apart. Avatar Section
          // keeps its own alignSelf:"center" below so only it stays centered.
          alignItems: "stretch",
          // The old centering approach had implicit slack above the content that
          // acted as breathing room; height:"100%" + flex-start removed that slack,
          // so the name started flush against the row's top ("hitting the ceiling").
          // This restores it explicitly, on the wrapper so content AND the right
          // column (timestamp) shift down together and stay lined up with each other.
          // Reduced from 1.75/1.75 (Rahim, 2026-09-08: "too much top and bottom
          // padding"). virtualItemSize below is shrunk by the same total (2x this
          // delta) so the row's actual rendered height shrinks with it instead of
          // just leaving extra blank space under a still-115/88px slot.
          pt: 1.25,
          pb: 1.25,
          cursor: "pointer",
          minWidth: 0,
        }}
      >
        {/* Avatar Section — doubles as the multi-select checkbox slot: hovering the avatar,
            or any row being checked (selection mode), swaps the avatar for a checkbox of
            the same footprint, so the row's layout never shifts. Clicks here toggle the
            selection instead of opening the chat. */}
        <Box
          onMouseEnter={canBulkSelect ? () => setAvatarHovered(true) : undefined}
          onMouseLeave={canBulkSelect ? () => setAvatarHovered(false) : undefined}
          onClick={showBulkCheckbox ? handleBulkToggle : undefined}
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            alignSelf: "center",
            // Tightened (Rahim, 2026-09-08: "make the name and the last chat text and
            // the lifecycle pill... close to the icon pfp") -- was 1/0.5 leaving a
            // visible gap before the text column.
            marginRight: { xs: 0.5, sm: 0.375, md: 0.5, lg: 0.5, xl: 0.5 },
            // Tracks the Avatar's own size below (was 58/50px reserving extra slack
            // beyond the actual avatar width, pushing the text column further right
            // than the avatar itself needed). Platform badge (PlatformIcon)
            // deliberately left untouched at its existing size=18.
            minWidth: { xs: "42px", sm: "36px", md: "42px", lg: "42px", xl: "42px" },
          }}
        >
          {showBulkCheckbox ? (
            // Same ListItemAvatar wrapper (MUI reserves its own min-width) and the same box size
            // as the Avatar below, so swapping avatar <-> checkbox never moves the text column.
            <ListItemAvatar sx={{ margin: 0 }}>
              <Box
                sx={{
                  width: { xs: 42, sm: 36, md: 42, lg: 42, xl: 42 },
                  height: { xs: 42, sm: 36, md: 42, lg: 42, xl: 42 },
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <BulkCheckbox
                  checked={isBulkChecked}
                  size={24}
                  inputProps={{ "aria-label": `Select conversation with ${contactNameLabel || "contact"}` }}
                  onClick={handleBulkToggle}
                />
              </Box>
            </ListItemAvatar>
          ) : (
          <ListItemAvatar sx={{ margin: 0 }}>
            {/* position:relative wrapper so the platform badge (WhatsApp/Instagram/
                Facebook) can sit pinned to the avatar's own corner, same convention
                as the active-status dot on InboxSettings.jsx's Team Member rows. */}
            <Box sx={{ position: "relative", display: "inline-flex" }}>
              <Avatar
                src={avatarImage}
                imgProps={NON_DRAGGABLE_AVATAR_IMG_PROPS}
                sx={{
                  // Shrunk from 50/40 (Rahim, 2026-09-08: "the pfp cartoonish icon are
                  // too big, make it small, dont change the size of the whatsapp
                  // facebook icon") -- PlatformIcon badge below is untouched.
                  width: { xs: 42, sm: 36, md: 42, lg: 42, xl: 42 },
                  height: { xs: 42, sm: 36, md: 42, lg: 42, xl: 42 },
                  bgcolor: (theme) =>
                    theme.palette.mode === "dark"
                      ? "rgba(255,255,255,0.07)"
                      : "rgba(0,0,0,0.05)",
                  // No selected-state color change here anymore — a green ring on the
                  // avatar read as an unwanted extra signal on top of the row's own
                  // selected-highlight background.
                  border: (theme) => `1px solid ${theme.palette.divider}`,
                }}
              />
              {/* PlatformIcon now draws its own colored circle + white glyph (the real
                  app-icon look) — this just pins it to the avatar's corner with a thin
                  paper-colored ring so it reads as a separate badge sitting on top. */}
              <Box
                sx={{
                  position: "absolute",
                  bottom: -2,
                  right: -2,
                  borderRadius: "50%",
                  // background.default (the row's own background), not .paper — paper is
                  // pure white/card-surface and stood out as a bright ring against the
                  // grey list background instead of blending in.
                  border: (theme) => `2px solid ${theme.palette.background.default}`,
                  // display:flex — see LiveCallCard.jsx's own comment on this same
                  // pattern: without it, this ring's shrink-to-fit sizing pulls in
                  // the surrounding font-size context via inline baseline rules,
                  // which can render it as a slight oval instead of a true circle.
                  display: "flex",
                }}
              >
                <PlatformIcon platform={message.platform || "whatsapp"} size={18} sx={{ ml: 0 }} />
              </Box>
            </Box>
          </ListItemAvatar>
          )}
        </Box>

        {/* Content Section — two full-width lines spanning the ENTIRE remaining
            row width (avatar's right edge all the way to the row's true right
            edge, same edge the "..." arrow sits at) instead of the old split
            where the timestamp/badges lived in their own separate fixed-width
            column next to a narrower name/preview column. That split is what
            caused the "gap before the date" complaint (the date's column had
            to match the WIDER badges-row beneath it) — and a first attempt at
            this fix (2026-09-09) nested the name+date row INSIDE the old,
            still-narrower content column instead of lifting it out to this
            full width, which glued the date right next to a short name with
            dead space on the right instead of at the true edge (Rahim: "the
            date is sticking to the left side with the name"). This wrapper is
            the actual fix: one flex:1 column, avatar-to-edge, holding both
            lines.
            Line A — name (grows, ellipsis) + timestamp (own width, hugs the
            true right edge). Line B — preview+lifecycle chip (grows, left) |
            unread count+status/24h badges (own width, right), matching the
            professional-CRM pattern (WhatsApp Web/Intercom/Front) discussed
            with Rahim: name+date share one line, decoupled from a second line
            for preview+badges. */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            flex: 1,
            minWidth: 0,
            height: "100%",
            // Negative, not the plain +8px gap this used to be -- MUI's
            // ListItemAvatar ships a hardcoded min-width:56px default (sized
            // for the classic 40px-avatar convention), never overridden when
            // the avatar here was shrunk to 42/44px, so the avatar's own
            // wrapper already reserves ~12px of pure dead space past the
            // avatar's real right edge before this margin even starts (Rahim,
            // 2026-09-09: "do you see this space at the right of the pfp
            // icon"). Pulling this column back by that same ~12px (leaving
            // the intended ~12px of real spacing -- the avatar wrapper's own
            // 4px marginRight plus this) closes the dead gap without
            // touching ListItemAvatar's own box (Rahim: "make the name...
            // position a little more to the left os the weird extra space is
            // filled").
            marginLeft: -0.5,
          }}
        >
          {/* Line A — name + timestamp. paddingRight on xs/sm only -- the "..."
              dropdown arrow (message-row-arrow below) is ALWAYS visible there
              (no hover on touch, so it can't fade in/out the way it does on
              md+), sitting absolutely positioned near this same top-right
              corner. Without reserved room, it rendered directly on top of
              the timestamp text (Rahim, 2026-09-10: phone view, "the dropdown
              icon... coming on top of the date text"). Sized to the arrow's
              own right-offset + width + a small gap so they never touch;
              md+ needs nothing extra since the arrow only appears on hover,
              replacing the timestamp rather than sitting beside it. */}
          <Box sx={{ display: "flex", alignItems: "center", width: "100%", gap: 0.75, paddingRight: { xs: "46px", sm: "46px", md: 0 }, boxSizing: "border-box" }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                // flex:1 + minWidth:0 -- the name now grows to fill whatever
                // real space is left before the timestamp (not a fixed/guessed
                // width), and can shrink small enough for its own ellipsis to
                // kick in only once it genuinely runs out of room.
                flex: 1,
                minWidth: 0,
                overflow: "hidden",
              }}
            >
              {showContactNameTooltip ? (
                <Tooltip title={rawContactName} arrow componentsProps={DARK_BUBBLE_TOOLTIP_SX}>
                  <Box component="span" sx={{ display: "inline-flex", maxWidth: "100%", minWidth: 0 }}>
                    {contactNameTypography}
                  </Box>
                </Tooltip>
              ) : (
                contactNameTypography
              )}
              {/* The QR number this chat is on (renders nothing for non-QR chats). */}
              <ChatNumberBadge connectionId={message.connectionId || message.lastConnectionId} sx={{ ml: 0.75 }} />
              {message.pinned && (
                <PushPinOutlinedIcon
                  sx={{
                    fontSize: "0.9rem",
                    color: (theme) => theme.palette.text.secondary,
                    flexShrink: 0,
                  }}
                />
              )}
            </Box>
            {/* Fades out on hover (md+, via isHovered -- see HoveredRowContext's
                comment at the top of the file for why this is JS-computed
                instead of a plain CSS "&:hover" on the row) so the
                dropdown-menu arrow can take this same spot instead of the two
                visually clashing/overlapping. flexShrink:0 -- own natural
                width (e.g. "Yesterday 8:29 PM"), never compressed. */}
            <Box
              className="message-row-timestamp"
              sx={{
                display: "flex",
                alignItems: "center",
                flexShrink: 0,
                transition: "opacity 0.15s ease-in-out",
                opacity: { md: isHovered ? 0 : 1 },
                pointerEvents: { md: isHovered ? "none" : "auto" },
              }}
            >
              <Typography
                variant="body2"
                color="textSecondary"
                // 0.75rem is this app's badge-text size, not meta/secondary-info
                // text — the app's own convention for a line like this is
                // 0.8125rem (Rahim, 2026-08-29: match our app font size).
                sx={{ fontSize: "0.8125rem", lineHeight: 1.2, whiteSpace: "nowrap" }}
              >
                {message.lastMessage ? formatTimestamp(message.lastMessage.timestamp) : ""}
              </Typography>
            </Box>
          </Box>

          {/* Line B — TWO independent full-width rows (not one shared two-
              column grid) for the exact same reason Line A had to be lifted
              out of the old shared-width column: a single grid forces both
              rows' right sides to the WIDEST one's content width. Concretely,
              the status+24h row (e.g. "ClusterdenQA" + "0h") is usually much
              wider than the unread-badge row (often just an invisible
              placeholder), so sharing one column squeezed the preview text
              down to whatever was left over from the WIDER row, even though
              the preview sits next to the narrow one (Rahim, 2026-09-09: "the
              last chat message... see the space right to the text its soo
              much space and it still says ..."). Row B1 = preview (grows) +
              unread badge (own width). Row B2 = lifecycle chip (grows) +
              status/24h (own width) -- each row's left side now only yields
              room to what's actually on ITS OWN right side. */}
          <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "flex-start", flex: 1, minWidth: 0 }}>
            {/* Spacer above Row B1, NOT an equal 50/50 split with the spacer
                below it (see the one after Row B1) -- flexGrow:2 vs flexGrow:3
                there. An even split measured geometrically equal (12.2px both
                sides) but still read as "more space above the text than below
                it" (Rahim, 2026-09-09) -- the bold/larger name text above has
                more visual leading baked into its own line-height than the
                plain preview text below has above the lifecycle chip, so an
                equal DOM gap doesn't look equal. Skewing the real gap
                slightly smaller on top compensates for that. */}
            <Box sx={{ flexGrow: 2 }} />

            {/* Row B1 — preview (grows) + unread count (own width) */}
            <Box sx={{ display: "flex", alignItems: "center", width: "100%", gap: 0.5 }}>
              {/* a small diagonal arrow on the LEFT now marks whether the
                  last message was sent by us (outgoing, ↗) or came from the contact
                  (incoming, ↙), using lastMessageFromContact, which inboxController.js
                  already computes via the app's own canonical isIncomingChatMessage()
                  classifier and sends with every contact — no extra fetch needed. Built
                  from a single ArrowUp icon (Bold variant — Linear read as too thin/slim)
                  rotated in CSS (45deg / 225deg) rather than two different icons, since
                  iconsax has no dedicated diagonal-arrow glyph. */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.375, flex: 1, minWidth: 0 }}>
                {message.lastMessage && (
                  <ArrowUp
                    size="11"
                    color={message.lastMessageFromContact ? theme.palette.text.secondary : theme.palette.primary.main}
                    variant="Linear"
                    style={{
                      flexShrink: 0,
                      transform: message.lastMessageFromContact ? "rotate(225deg)" : "rotate(45deg)",
                      // Linear variant back (Bold changed the shape too much) — this is
                      // just a slight faux-bold thickening of the same thin icon instead,
                      // via a same-color drop-shadow rather than a real strokeWidth prop
                      // (iconsax doesn't expose one).
                      filter: `drop-shadow(0 0 0.4px ${message.lastMessageFromContact ? theme.palette.text.secondary : theme.palette.primary.main})`,
                    }}
                  />
                )}
                {/* Hover reveals the full preview text — the row itself always clips it
                    with an ellipsis at this width (Rahim, 2026-09-01: "when hovered on
                    the last message... it should show the last message"). Empty-string
                    title is fine — MUI Tooltip just doesn't open for one.
                    flex:1 + minWidth:0 goes on this OUTER Box (claims the row's real
                    available width, same responsive-ellipsis approach as the name
                    above) -- NOT on the Typography Tooltip actually wraps. A short
                    preview ("eee") left flex:1 stretching the Typography itself to
                    the full claimed width, so MUI centered the tooltip under that
                    whole empty box instead of the actual few-pixel-wide text glyph
                    (Rahim, 2026-09-09: "tooltip is not at correct position").
                    display:"inline-block" + maxWidth:"100%" on the Typography make
                    its own box hug just the rendered (possibly ellipsized) text,
                    same shrink-wrap trick the name's own Tooltip target already
                    uses via its span wrapper. */}
                <Box sx={{ flex: "1 1 auto", minWidth: 0, overflow: "hidden" }}>
                  <Tooltip title={messagePreview || ""} arrow componentsProps={DARK_BUBBLE_TOOLTIP_SX}>
                    <Typography
                      variant="body2"
                      component="span"
                      sx={{
                        display: "inline-block",
                        maxWidth: "100%",
                        verticalAlign: "bottom",
                        color: (theme) => theme.palette.text.secondary,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        fontSize: "0.8125rem",
                        lineHeight: 1.2,
                      }}
                    >
                      {messagePreview}
                    </Typography>
                  </Tooltip>
                </Box>
              </Box>

              {/* Message count (unread) — own width, right-aligned on this row.
                  Always mounted, hidden via visibility (not unmounted / display:none)
                  when there's nothing to show — unmounting it would change this
                  row's layout the instant a contact gets selected (isSelected
                  forces visibleUnreadCount to 0), and with the cursor still
                  parked from the click that just selected it, that reflow
                  shifts the timestamp/arrow hover zone under a stationary
                  pointer, flipping the row's hover boundary and reading as the
                  timestamp flickering against the "..." dropdown arrow (Rahim,
                  2026-08-27: "leave my cursor on it... starts flicking with the
                  drop down"). visibility:hidden keeps the exact same box
                  occupying the exact same space either way. */}
              <Box
                sx={{
                  visibility: visibleUnreadCount > 0 ? "visible" : "hidden",
                  bgcolor: (theme) =>
                    theme.palette.mode === "dark" ? theme.palette.success.dark : theme.palette.success.main,
                  color: (theme) => theme.palette.common.white,
                  height: { xs: 14, sm: 14, md: 18, lg: 20, xl: 22 },
                  // minWidth, not width — a raw 1-2 digit count still renders as a
                  // perfect circle (width naturally equals minWidth); a capped
                  // label like "99+"/"999+"/"9999+" widens into a pill instead of
                  // clipping in this small circle. A true fixed circle was tried
                  // first (matching InboxSettings.jsx's getCountBadgeSx) but even
                  // the smallest legible font still clipped "9999+" at this row
                  // badge's smaller size — confirmed via computed
                  // scrollWidth>clientWidth — so pill it is here (Rahim,
                  // 2026-08-29: "make pill thats uch better").
                  minWidth: { xs: 14, sm: 14, md: 18, lg: 20, xl: 22 },
                  px: unreadBadgeLabel.length > 2 ? "4px" : 0,
                  fontSize: { xs: "0.625rem", sm: "0.625rem", md: "0.75rem", lg: "0.8125rem", xl: "0.875rem" },
                  borderRadius: "999px",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  fontWeight: "bold",
                  flexShrink: 0,
                }}
              >
                {unreadBadgeLabel}
              </Box>
            </Box>

            {/* Spacer below Row B1 -- flexGrow:3 vs the spacer above's
                flexGrow:2 (see its own comment) skews the real gap slightly
                bigger here than above, to counter the preview text's own
                leading and read as visually centered rather than
                geometrically centered (Rahim, 2026-09-09). */}
            <Box sx={{ flexGrow: 3 }} />

            {/* Row B2 — Lifecycle stage (grows) + assignment status/24h-window
                pill (own width). Backend includes lifecycleStageId
                (2026-08-19 fix, see inboxController.js) — renders nothing
                when the contact has no stage set (Rahim's call). minHeight:22px
                (the chip's own real rendered height) reserves this row's space
                even when LifecycleStageChip renders null -- otherwise Row B1
                above (this column's own flexGrow-spacer split) would drift
                and read as off-center (2026-09-09, Rahim: "not center
                aligned"). */}
            <Box sx={{ display: "flex", alignItems: "center", width: "100%", gap: 0.5 }}>
              <Box sx={{ flex: 1, minWidth: 0, minHeight: "22px" }}>
                <LifecycleStageChip
                  contactId={message.contactId}
                  lifecycleStageId={message.lifecycleStageId}
                  onChanged={(newStageId) => onLifecycleChanged(message.contactId, newStageId)}
                  // isHovered here too (not just isSelected) -- this used to
                  // come from a cross-component CSS selector (the row's own
                  // "&:hover .lifecycle-stage-chip" rule overriding this
                  // component's background), which is exactly the mechanism
                  // that went stale on a virtualized-list reorder (see
                  // HoveredRowContext's comment, top of file). forceLightBg
                  // already produces the identical light background this
                  // hover-tint needs, so driving it from real JS state here
                  // removes the fragile cross-component CSS dependency
                  // entirely instead of just patching around it.
                  forceLightBg={isSelected || isHovered}
                />
              </Box>

              {/* Assignment status — a slightly-rounded rectangle (not a capsule).
                  Same displayStatus/statusChipLabel data as before. The 24h
                  WhatsApp-window pill (Clock icon + "Xh") sits to its right,
                  same activeStatus data/look, still no click handler.
                  flexShrink:0 -- own natural content width (status chip + hour
                  chip), independent of Row B1's own right side above it.
                  Hidden entirely for a group row (WhatsApp Groups plan decision #2 — no
                  assignment/status model exists for a group, so "All" would be a
                  meaningless label here, not a real Assigned/Unassigned/Closed state). */}
              {!message.isGroup && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0, flexShrink: 0 }}>
                <Tooltip title={statusChipTooltip} arrow componentsProps={DARK_BUBBLE_TOOLTIP_SX}>
                  <Box
                    sx={{
                      bgcolor:
                        displayStatus === "Closed"
                          ? (theme) => (theme.palette.mode === "dark" ? theme.palette.success.dark : theme.palette.success.main)
                          : displayStatus === "Unassigned"
                          ? (theme) => (theme.palette.mode === "dark" ? theme.palette.warning.dark : theme.palette.warning.main)
                          : displayStatus === "Assigned"
                          ? (theme) => theme.palette.primary.main
                          : (theme) => theme.palette.info.main,
                      color: (theme) => theme.palette.common.white,
                      fontSize: "0.75rem",
                      fontWeight: 500,
                      lineHeight: 1.4,
                      borderRadius: "6px",
                      padding: "2px 7px",
                      // flex (not a fixed maxWidth) — lets this genuinely shrink
                      // to whatever's left after the hour-pill next to it claims
                      // its own fixed size, instead of a static cap that doesn't
                      // respond to the row actually having less room now that
                      // two things share it (Rahim, 2026-08-29: "gets distorted").
                      flex: "1 1 auto",
                      minWidth: 0,
                      // This column is flexShrink:0, so the chip itself caps how wide a long
                      // assignee (e.g. a full team name) can get before the ellipsis kicks in.
                      maxWidth: 112,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {statusChipLabel}
                  </Box>
                </Tooltip>
                {/* No icon on the pill itself — digit + "h" only (Rahim,
                    2026-08-29: "remove the clock... only use digit and h") — but
                    what it MEANS isn't self-evident from "0h"/"24h" alone, so the
                    explanation moved into a tooltip instead (Rahim, 2026-08-29:
                    "use a tooltip that defines 24h window pill as well").
                    Not rendered for website chat or a QR-connected workspace's WhatsApp chats — no 24h window there (Instagram / Facebook
                    chats of a QR-only workspace keep theirs: hasMetaServiceWindow). */}
                {hasMetaServiceWindow({ platform: message.platform, isQrConnectedWorkspace }) && (
                <Tooltip
                  title={
                    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                      <span>24-hour window</span>
                      <span style={{ opacity: 0.7 }}>Meta's reply-window rule</span>
                    </Box>
                  }
                  componentsProps={DARK_BUBBLE_TOOLTIP_SX}
                >
                <Chip
                  label={`${message.activeStatus}h`}
                  sx={{
                    bgcolor: (theme) => theme.palette.divider,
                    // Bigger + tighter horizontal padding below — closer to a
                    // square silhouette instead of a flat wide strip (Rahim,
                    // 2026-08-29: "a little big and more like a square").
                    height: "20px",
                    fontSize: "0.75rem",
                    minWidth: "auto",
                    flexShrink: 0,
                    color: (theme) => theme.palette.text.primary,
                    fontWeight: "500",
                    // Square with rounded corners, matching the status badge beside it —
                    // not MUI Chip's default full-pill capsule shape (Rahim, 2026-08-29).
                    borderRadius: "6px",
                    "& .MuiChip-label": { padding: "0 6px" },
                  }}
                />
                </Tooltip>
                )}
              </Box>
              )}
            </Box>
          </Box>
        </Box>
      </Box>

      {/* WhatsApp Web-style dropdown arrow. Was pure CSS "&:hover", no JS
          state -- switched to isHovered (see HoveredRowContext's comment,
          top of file) because this list is virtualized and "Newest"-sorted,
          so a reorder could slide a different contact's row under a
          stationary cursor without any real mouse event firing, leaving
          this arrow (and the timestamp it swaps with) showing the wrong
          state until the mouse physically moved (Rahim, 2026-09-09). */}
      <Box
        className="message-row-arrow"
        sx={{
          position: "absolute",
          right: {
            xs: `calc(3px + ${theme.spacing(1)} + 6px)`,
            sm: "6px",
            md: "6px",
            lg: "6px",
            xl: "6px",
          },
          // Matches the row's own top padding (was a flat 0, ignoring it — sat above
          // the name instead of level with it, since it's absolutely positioned
          // against the whole row, not the padded content inside it).
          // Retuned after fixing the wrapper-height bug above — that fix changed
          // the name's actual position (now consistent in both cases at 15.4px
          // from the row's top, live-measured), so this needed recalibrating
          // against the new, correct baseline rather than the old inconsistent one.
          top: "15px",
          zIndex: 10,
          // xs/sm always show the arrow (no hover on touch) regardless of
          // isHovered; only md+ ties it to the JS-computed hover state.
          opacity: { xs: 1, sm: 1, md: isHovered ? 1 : 0 },
          pointerEvents: { xs: "auto", sm: "auto", md: isHovered ? "auto" : "none" },
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          transition: "opacity 0.15s ease-in-out",
        }}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <IconButtonWithTooltip
          label="More options"
          data-contact-id={message.contactId}
          size="small"
          onClick={handleMenuClick}
          onMouseDown={(e) => {
            e.stopPropagation();
            e.preventDefault();
          }}
          sx={{
            color: (theme) => theme.palette.text.secondary,
            backgroundColor: "transparent",
            border: "none",
            boxShadow: "none",
            padding: { xs: "2px", sm: "2px", md: 0 },
            margin: 0,
            minWidth: { xs: "30px", sm: "30px", md: "28px" },
            width: { xs: "30px", sm: "30px", md: "28px" },
            height: { xs: "30px", sm: "30px", md: "28px" },
            transition: "color 0.15s ease-in-out",
            "&:hover": {
              backgroundColor: "transparent",
              color: (theme) => theme.palette.primary.main,
            },
          }}
        >
          {/* color="currentColor" (not a fixed theme value) so the IconButton's own
              hover color-change below (text.secondary -> primary.main) still works —
              iconsax icons don't inherit color from their parent unless told to. */}
          <ArrowDown2 size="20" color="currentColor" variant="Bold" />
        </IconButtonWithTooltip>
      </Box>
    </ListItem>
  );
});

MessageItem.displayName = 'MessageItem';

// Custom comparison — hover is CSS-only (no JS state), menu state comes from context (not props).
// Only re-render when the row's actual data or selection changes.
const areEqual = (prevProps, nextProps) => {
  const messageChanged =
    prevProps.message.contactId !== nextProps.message.contactId ||
    prevProps.message.pinned !== nextProps.message.pinned ||
    prevProps.message.name !== nextProps.message.name ||
    prevProps.message.status !== nextProps.message.status ||
    prevProps.message.lastMessage?.timestamp !== nextProps.message.lastMessage?.timestamp ||
    prevProps.message.lastMessage?.content !== nextProps.message.lastMessage?.content ||
    prevProps.message.lastMessage?.body !== nextProps.message.lastMessage?.body ||
    prevProps.message.lastMessage?.message !== nextProps.message.lastMessage?.message ||
    prevProps.message.lastMessage?.type !== nextProps.message.lastMessage?.type ||
    prevProps.message.lastMessage?.messageType !== nextProps.message.lastMessage?.messageType ||
    prevProps.message.receivedCount !== nextProps.message.receivedCount ||
    prevProps.message.platform !== nextProps.message.platform ||
    prevProps.message.connectionId !== nextProps.message.connectionId ||
    prevProps.message.lastConnectionId !== nextProps.message.lastConnectionId ||
    prevProps.message.activeStatus !== nextProps.message.activeStatus ||
    prevProps.message.lastMessageFromContact !== nextProps.message.lastMessageFromContact ||
    prevProps.message.assignedToName !== nextProps.message.assignedToName ||
    prevProps.message.assignedTo !== nextProps.message.assignedTo ||
    prevProps.message.assignedToIsAi !== nextProps.message.assignedToIsAi ||
    prevProps.message.lifecycleStageId !== nextProps.message.lifecycleStageId;

  const otherPropsChanged =
    prevProps.isSelected !== nextProps.isSelected ||
    prevProps.index !== nextProps.index ||
    prevProps.bulkSelectable !== nextProps.bulkSelectable ||
    prevProps.assigneeIsTeam !== nextProps.assigneeIsTeam;

  return !messageChanged && !otherPropsChanged;
};

const MemoizedMessageItem = React.memo(MessageItem, areEqual);

const MessageCard = ({ searchTerm, isSearching, selectedLabels, contactsData, activeFilter, unrepliedOnly, onLoadMore, hasMore, isLoadingMore, showLoadMoreButton, onExplicitLoadMore, isBlockedTab }) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isSmOnly = useMediaQuery(theme.breakpoints.between("sm", "md"));
  const userDetails = useSelector(selectUserDetailsData);
  const { selectContact, selectGroup } = useContactSelection();
  const { guard } = useSubscriptionGuard();
  const data = useSelector(selectInboxRecentData);
  const loading = useSelector(selectInboxRecentLoading);
  const error = useSelector(selectInboxRecentError);
  const initialized = useSelector(selectInboxRecentInitialized);
  const selectedContactId = useSelector(selectContactID);
  const deepLinkContactInfo = useSelector(selectContactInfo);
  const openingCustomerContactId = useSelector(
    (state) => state.inboxMessage.expectedContactId
  );
  const urlContactSyncSuppressUntil = useSelector(
    (state) => state.inboxAll.urlContactSyncSuppressUntil || 0
  );
  const lazyTabsLoaded = useSelector(selectInboxRecentLazyTabsLoaded);
  const selectedInboxTab = useSelector(selectSelectedInboxTab);
  // Multi-select only ever applies to the list the user is looking at — switching tab, the
  // Show filter, Unreplied, or workspace starts from an empty selection, and leaving the
  // Inbox drops it entirely (rows from another list must never ride along into a bulk job).
  const bulkWorkspaceId = useSelector((s) => s.workspace?.currentWorkspace?._id);
  React.useEffect(() => {
    dispatch(clearBulkSelection());
  }, [dispatch, selectedInboxTab, activeFilter, unrepliedOnly, isBlockedTab, bulkWorkspaceId]);
  React.useEffect(() => () => dispatch(clearBulkSelection()), [dispatch]);
  const skipTabAutoCorrectContactId = useSelector((state) => state.inbox.skipNextTabAutoCorrectContactId);
  const hasDispatchedRef = React.useRef(false);
  const loadMorePendingRef = React.useRef(false);
  React.useEffect(() => {
    if (!isLoadingMore) loadMorePendingRef.current = false;
  }, [isLoadingMore]);
  const navigate = useNavigate();
  const { accountid, userid, contactid } = useParams();
  const location = useLocation();

  const urlSyncedContactIdRef = React.useRef(null);
  const recentlyDeletedContactRef = React.useRef(null);
  const recentlyDeletedClearTimerRef = React.useRef(null);
  const lazyFetchAttemptedForRef = React.useRef(null);
  const tabCorrectedForRef = React.useRef(null);

  React.useEffect(() => {
    if (!contactid) {
      urlSyncedContactIdRef.current = null;
      lazyFetchAttemptedForRef.current = null;
      if (typeof window !== "undefined" && !window.location.pathname.includes("/chat/")) {
        dispatch(clearUrlContactSyncSuppress());
      }
      return;
    }
    if (Date.now() < urlContactSyncSuppressUntil) return;
    if (
      recentlyDeletedContactRef.current &&
      String(recentlyDeletedContactRef.current) === String(contactid)
    ) return;
    if (
      hasDispatchedRef.current &&
      !loading &&
      data &&
      !checkContactExistsInInboxData(data, contactid)
    ) {
      // Closed/Blocked buckets are lazy-loaded only on manual tab click, so a deep
      // link (Send Message button, notification, refresh) to a Closed/Blocked
      // contact always reads as "not found" above. Trigger the same lazy fetch
      // once per contactid so the existence check gets a fair chance once it lands.
      const bothLazyTabsSettled = lazyTabsLoaded.Closed && lazyTabsLoaded.Blocked;
      if (!bothLazyTabsSettled) {
        const idStrForFetch = String(contactid);
        if (lazyFetchAttemptedForRef.current !== idStrForFetch) {
          lazyFetchAttemptedForRef.current = idStrForFetch;
          if (!lazyTabsLoaded.Closed) dispatch(fetchClosedTabInbox("Closed"));
          if (!lazyTabsLoaded.Blocked) dispatch(fetchClosedTabInbox("Blocked"));
        }
      }
      return;
    }
    const idStr = String(contactid);
    if (urlSyncedContactIdRef.current === idStr) return;
    // Already loaded (e.g. selection happened elsewhere — like the Dashboard's contact list —
    // before this MessageCard instance ever mounted, so its own urlSyncedContactIdRef/
    // selectedContactIdRef start out blank and can't otherwise tell). Without this check,
    // selectContact(..., clearPrevious:true) wipes the just-loaded messages via flushSync, and
    // the resulting refetch gets silently swallowed by fetchMessagesByContact's per-contact
    // debounce — leaving the chat permanently blank while selectedContactID (and so ChatInfo)
    // stays set.
    if (selectedContactId && String(selectedContactId) === idStr) {
      urlSyncedContactIdRef.current = idStr;
      return;
    }
    // Bug fix: a deep-linked/refreshed URL doesn't say whether its id is a contact or a
    // group, so this has to check data.All to tell them apart (see below) -- but data.All
    // starts as an empty array before the first fetchInbox() resolves, `data` itself is
    // never null/falsy (initial Redux state is { All: [], You: [], ... }), so checking
    // `data` alone can't tell "not loaded yet" apart from "genuinely no groups". Committing
    // to the wrong selectContact/selectGroup call on that empty-array false negative — and
    // caching the decision via urlSyncedContactIdRef just below — could never self-correct
    // even once the real data landed, since the ref-based re-entry guard above (`if
    // (urlSyncedContactIdRef.current === idStr) return`) blocks any later retry. `initialized`
    // (already used elsewhere in this file, e.g. the empty-state check below) is the actual
    // "has the first real fetch resolved" signal -- wait for it before ever committing.
    if (!initialized) return;
    urlSyncedContactIdRef.current = idStr;
    // groups already appear inline in data.All (see inboxController.js's Phase G6 merge),
    // which is eagerly loaded unlike the dedicated Groups tab, so this check works even
    // before that tab is ever clicked.
    const isUrlGroup = data?.All?.some((item) => item.contactId === idStr && item.isGroup);
    if (isUrlGroup) {
      selectGroup(contactid, { navigate: false, clearPrevious: true });
    } else {
      selectContact(contactid, { navigate: false, clearPrevious: true });
    }
  }, [contactid, selectContact, selectGroup, loading, data, initialized, dispatch, urlContactSyncSuppressUntil, selectedContactId, lazyTabsLoaded.Closed, lazyTabsLoaded.Blocked]);

  // Once a deep-linked contact is confirmed to live in Closed or Blocked (whether that
  // took a lazy fetch above, or the bucket was already cached from earlier this session),
  // correct the sidebar tab to match — otherwise the chat opens while the sidebar still
  // highlights "All" and the contact is invisible in the currently-rendered list.
  // Deliberately gated on selectedContactId already matching this contact (i.e. the chat
  // is already confirmed open) before touching selectedInboxTab at all: switching the tab
  // any earlier — while the chat-open flow above is still mid-flight — would change
  // selectedInboxTab out from under ChatBody's own contactExists-driven effects at a moment
  // this codebase was never exercised at before (Closed/Blocked previously never became
  // "exists" while a selection was still settling), risking exactly the kind of stale-response
  // race InboxMessageSlice's "late response after clearMessages()" guard exists to catch.
  React.useEffect(() => {
    if (!contactid) {
      tabCorrectedForRef.current = null;
      return;
    }
    const idStr = String(contactid);
    if (tabCorrectedForRef.current === idStr) return;
    if (!data) return;
    if (!selectedContactId || String(selectedContactId) !== idStr) return;
    const inClosed = Array.isArray(data.Closed) && data.Closed.some(
      (c) => String(c.contactId || c.contactID) === idStr
    );
    const inBlocked = !inClosed && Array.isArray(data.Blocked) && data.Blocked.some(
      (c) => String(c.contactId || c.contactID) === idStr
    );
    // Rahim, 2026-09-18: "when i close a convo i still want ... the left panel to [not]
    // change me to close panel" — socketMessageDispatcher.js's "closechat" handler sets this
    // flag only when the closed contact IS the one currently open, so this correction (built
    // for deep-linking into an ALREADY-closed/blocked chat) skips itself for that one contact
    // instead of jumping the sidebar. tabCorrectedForRef is still marked so this doesn't retry
    // in a fetch-driven re-run of this effect.
    if (inClosed || inBlocked) {
      if (skipTabAutoCorrectContactId && String(skipTabAutoCorrectContactId) === idStr) {
        tabCorrectedForRef.current = idStr;
        dispatch(clearSkipTabAutoCorrect());
        return;
      }
    }
    if (inClosed) {
      tabCorrectedForRef.current = idStr;
      if (selectedInboxTab !== "Closed") {
        dispatch(markSkipNextTabChangeClear());
        dispatch(setSelectedInboxTab("Closed"));
      }
    } else if (inBlocked) {
      tabCorrectedForRef.current = idStr;
      if (selectedInboxTab !== "Blocked Contacts") {
        dispatch(markSkipNextTabChangeClear());
        dispatch(setSelectedInboxTab("Blocked Contacts"));
      }
    }
  }, [contactid, data, selectedInboxTab, selectedContactId, skipTabAutoCorrectContactId, dispatch]);

  // Deep-link into a Closed/Blocked chat from outside the inbox (Reports, Contacts, ...) while the
  // sidebar was on a tab whose Closed/Blocked bucket was never loaded. The effects above only learn a
  // contact is Closed/Blocked by finding it inside the lazily-loaded buckets, and those can be late,
  // never triggered (their re-run depends on fetchInbox toggling `loading`), or first-page-only
  // (limit 50), so such a chat could open with the sidebar still on "All" or be dropped altogether.
  // The opened contact's own status (contactInfo, fetched alongside the messages) is authoritative:
  // use it to load the right bucket, and if the contact still isn't in it (older than page 1), switch
  // the sidebar tab directly. Only acts for a contact that is NOT in any loaded list, once per
  // contact, so normal in-list opens/clicks and the live-close "don't jump tabs" rule are untouched.
  // skipNextTabAutoCorrectContactId is meant only for a chat closed LIVE while this component stays
  // mounted. A copy left over from an earlier close (consumed by nothing once the user navigated
  // away) would make a later deep link to that same contact skip its tab correction, so drop any
  // stale one when this component mounts already pointing at a chat URL.
  React.useEffect(() => {
    if (contactid) dispatch(clearSkipTabAutoCorrect());
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only
  }, []);
  // Tab hint passed by pages outside the Inbox (see utils/openContactInInbox.js): that page already
  // knows this contact is Closed/Blocked. Remember it per contact (a later URL replace drops router
  // state) and apply it through the same skip-flag + tab switch as the detection effect above, the
  // moment the chat selection lands -- no need to wait for the lists / contact info.
  const tabHintRef = React.useRef({ id: null, tab: null });
  const hintedTab = location.state?.inboxTab;
  React.useEffect(() => {
    if (!contactid) {
      tabHintRef.current = { id: null, tab: null };
      return;
    }
    const idStr = String(contactid);
    if (hintedTab === "Closed" || hintedTab === "Blocked Contacts") {
      if (tabHintRef.current.id !== idStr) tabHintRef.current = { id: idStr, tab: hintedTab, applied: false };
    }
    const hint = tabHintRef.current;
    if (hint.id !== idStr || hint.applied) return;
    if (!selectedContactId || String(selectedContactId) !== idStr) return;
    hint.applied = true;
    if (selectedInboxTab !== hint.tab) {
      dispatch(markSkipNextTabChangeClear());
      dispatch(setSelectedInboxTab(hint.tab));
    }
  }, [contactid, hintedTab, selectedContactId, selectedInboxTab, dispatch]);

  const deepLinkResolvedForRef = React.useRef(null);
  React.useEffect(() => {
    if (!contactid) {
      deepLinkResolvedForRef.current = null;
      return;
    }
    const idStr = String(contactid);
    if (!selectedContactId || String(selectedContactId) !== idStr) return;
    if (!data || !initialized) return;
    if (checkContactExistsInInboxData(data, idStr)) return;
    if (!deepLinkContactInfo || String(deepLinkContactInfo._id || "") !== idStr) return;
    if (skipTabAutoCorrectContactId && String(skipTabAutoCorrectContactId) === idStr) return;
    const bucket = deepLinkContactInfo.isBlocked
      ? "Blocked"
      : deepLinkContactInfo.status === "Closed"
        ? "Closed"
        : null;
    if (!bucket) return;
    if (!lazyTabsLoaded[bucket]) {
      dispatch(fetchClosedTabInbox(bucket));
      return;
    }
    if (deepLinkResolvedForRef.current === idStr) return;
    deepLinkResolvedForRef.current = idStr;
    const tab = bucket === "Blocked" ? "Blocked Contacts" : "Closed";
    if (selectedInboxTab !== tab) {
      dispatch(markSkipNextTabChangeClear());
      dispatch(setSelectedInboxTab(tab));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- depends on lazyTabsLoaded.Closed/.Blocked only, not the whole object
  }, [contactid, selectedContactId, data, initialized, deepLinkContactInfo, lazyTabsLoaded.Closed, lazyTabsLoaded.Blocked, selectedInboxTab, skipTabAutoCorrectContactId, dispatch]);

  const invalidUrlStripRef = React.useRef(false);
  React.useEffect(() => { invalidUrlStripRef.current = false; }, [contactid]);

  React.useEffect(() => {
    if (!contactid) return;
    if (!hasDispatchedRef.current) return;
    if (loading || error) return;
    if (!data) return;
    if (checkContactExistsInInboxData(data, contactid)) return;
    // Don't strip the URL until both Closed/Blocked lazy buckets have actually been
    // checked — fetchClosedTabInbox doesn't toggle the `loading` flag above, so without
    // this the strip below could fire while that fetch is still in flight.
    if (!(lazyTabsLoaded.Closed && lazyTabsLoaded.Blocked)) return;
    // checkContactExistsInInboxData only looks at the already-loaded inbox buckets —
    // Closed/Blocked are lazy-loaded on first tab click and stay [] until then, so a
    // contact from one of those (or otherwise outside the currently-fetched inbox
    // categories) reads as "not found" here even right after its messages have
    // genuinely loaded. selectedContactId is messages.contactID (see selectContactID)
    // and openingCustomerContactId is the fetch-in-flight signal (expectedContactId) —
    // together the same messagesMatchSelected / fetchInFlightForSelected escape hatch
    // Inbox.jsx's analogous existence check uses. Without it this effect strips the
    // URL/selection the instant such a contact's chat finishes loading (visible as:
    // chat flashes open, then reverts to "No conversation selected").
    if (selectedContactId && String(selectedContactId) === String(contactid)) return;
    if (openingCustomerContactId != null && String(openingCustomerContactId) === String(contactid)) return;
    if (invalidUrlStripRef.current) return;
    invalidUrlStripRef.current = true;
    console.warn("[DL-TRACE] MessageCard invalid-URL strip", { contactid, selectedContactId, openingCustomerContactId, lazyClosed: lazyTabsLoaded.Closed, lazyBlocked: lazyTabsLoaded.Blocked });
    dispatchInboxSidebarTabChangeClear(dispatch);
    const effectiveUserid = userid || userDetails?._id;
    navigateOffCustomerChatRoute(navigate, accountid, effectiveUserid);
  }, [contactid, loading, error, data, dispatch, navigate, accountid, userid, userDetails?._id, selectedContactId, openingCustomerContactId, lazyTabsLoaded.Closed, lazyTabsLoaded.Blocked]);

  React.useEffect(() => {
    const handleAssignmentUpdate = (event) => {
      const { contactId, contactID, assignedTo, assignedToName, assignedToId, status } = event.detail || {};
      const targetContactId = contactId || contactID;
      if (targetContactId && (assignedToName !== undefined || assignedTo !== undefined || status !== undefined)) {
        dispatch(updateAssignmentInInbox({ contactId: targetContactId, assignedTo, assignedToName, assignedToId, status }));
        invalidateCachedContact(targetContactId);
      }
    };
    window.addEventListener('assignmentUpdate', handleAssignmentUpdate);
    window.addEventListener('contactStatusUpdate', handleAssignmentUpdate);
    return () => {
      window.removeEventListener('assignmentUpdate', handleAssignmentUpdate);
      window.removeEventListener('contactStatusUpdate', handleAssignmentUpdate);
    };
  }, [dispatch]);

  // Menu state — anchorPosition for virtualization compatibility
  const [menuAnchorPosition, setMenuAnchorPosition] = React.useState(null);
  const [menuOpenContactId, setMenuOpenContactId] = React.useState(null);
  // Matches the row's own minHeight (115/88 -- Rahim, 2026-09-08: "make the
  // height back to what it was") — react-window's fixed itemSize has to track
  // the CSS minHeight or rows overlap/clip regardless of what the CSS says.
  const virtualItemSize = isSmOnly ? 88 : 115;

  const [confirmDialog, setConfirmDialog] = React.useState({
    open: false,
    type: null,
    contactId: null,
    contactName: null,
    isGroup: false,
  });

  const handleMenuOpen = React.useCallback((point, contactId) => {
    // point = {x, y} from the click event itself (see handleMenuClick) — not
    // an element to measure. A real click always has real coordinates, so
    // there's nothing left to validate the way the old getBoundingClientRect
    // path needed (Rahim, 2026-08-21).
    if (!point || typeof point.x !== "number" || typeof point.y !== "number") return;
    setMenuAnchorPosition({ top: point.y, left: point.x });
    setMenuOpenContactId(contactId);
  }, []);

  const handleMenuClose = React.useCallback((event, reason) => {
    if (reason === 'backdropClick' || reason === 'escapeKeyDown' || reason === 'menuItemClick') {
      setMenuAnchorPosition(null);
      setMenuOpenContactId(null);
    }
  }, []);

  // Container ref and height — single ResizeObserver, no setTimeout chains
  const containerRef = React.useRef(null);
  const resizeObserverRef = React.useRef(null);
  const [containerHeight, setContainerHeight] = React.useState(0);

  const containerRefCallback = React.useCallback((node) => {
    // Clean up previous observer whenever the ref changes
    if (resizeObserverRef.current) {
      resizeObserverRef.current.disconnect();
      resizeObserverRef.current = null;
    }
    containerRef.current = node;
    if (!node) return;

    const measure = () => {
      const h = node.getBoundingClientRect().height || node.clientHeight;
      if (h > 0) setContainerHeight(h);
    };

    // One rAF to let the browser finish its first layout pass
    requestAnimationFrame(measure);

    if (typeof ResizeObserver !== 'undefined') {
      resizeObserverRef.current = new ResizeObserver(() => requestAnimationFrame(measure));
      resizeObserverRef.current.observe(node);
      const parent = node.parentElement;
      if (parent) resizeObserverRef.current.observe(parent);
    }
  }, []);

  // Cleanup observer on unmount
  React.useEffect(() => {
    return () => {
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
        resizeObserverRef.current = null;
      }
    };
  }, []);

  React.useEffect(() => {
    if (!userDetails) dispatch(fetchUserDetails());
  }, [dispatch, userDetails]);

  React.useEffect(() => {
    if (userDetails && !loading && !hasDispatchedRef.current) {
      dispatch(fetchInbox());
      hasDispatchedRef.current = true;
    }
  }, [dispatch, userDetails, loading]);

  const messageCard = React.useMemo(
    () => (contactsData ? transformMessages(contactsData) : []),
    [contactsData]
  );

  // Used to only filter by selectedLabels here -- the searchTerm-based
  // filter that used to live in this memo was a REDUNDANT second pass:
  // contactsData is already the exact search result by the time it reaches
  // here (InboxAll.jsx's contactsToShow), so re-filtering by the same term
  // again could only ever narrow it further by coincidence, never
  // correctly, and was part of what made search feel like it was
  // re-triggering on every keystroke (2026-09-08 removal).
  const filteredMessages = React.useMemo(() => {
    if (!messageCard.length) return [];
    let filtered = messageCard;
    if (selectedLabels && selectedLabels.length > 0) {
      filtered = filtered.filter((message) => {
        if (!message.labels || !Array.isArray(message.labels)) return false;
        return selectedLabels.some((selectedLabel) => message.labels.includes(selectedLabel));
      });
    }
    return filtered;
  }, [messageCard, selectedLabels]);

  // While multi-select is active, publish which rows of this list can be selected (no group
  // chats, nothing on the Blocked tab) for the bulk bar's select-all checkbox. Skipped entirely
  // when selection isn't active, so normal list updates don't touch the store.
  const bulkSelectionActiveForList = useSelector(selectBulkSelectionActive);
  React.useEffect(() => {
    if (!bulkSelectionActiveForList) return;
    const ids = isBlockedTab
      ? []
      : filteredMessages.filter((m) => m && !m.isGroup && m.contactId).map((m) => String(m.contactId));
    dispatch(setBulkSelectableIds(ids));
  }, [dispatch, bulkSelectionActiveForList, filteredMessages, isBlockedTab]);

  // Hover tracking — see HoveredRowContext's own comment (top of file) for
  // why this can't just be CSS `:hover`/onMouseEnter, and for why it's a
  // tiny external store rather than useState: setting React state here would
  // re-render MessageCard (and, via a Context value, every visible row)
  // on every single hover-target change, which is exactly the "hover feels
  // slow now" regression Rahim caught (2026-09-09). The store's own
  // setHoveredContactId notifies ONLY the rows actually subscribed to a
  // changed value — MessageCard itself never needs to re-render for hover
  // at all, so it's created once via a ref, not state.
  //
  // outerListRef is react-window's own scrollable element (its `outerRef`),
  // giving us scrollTop + getBoundingClientRect for the Y-to-row math.
  // lastMouseClientYRef holds the last real clientY seen over the list, so
  // the effect below can re-derive "who's actually under the cursor" after
  // a reorder without needing the mouse to move again.
  const hoverStoreRef = React.useRef(null);
  if (!hoverStoreRef.current) hoverStoreRef.current = createHoverStore();
  const lastMouseClientYRef = React.useRef(null);
  const outerListRef = React.useRef(null);

  const computeHoveredContactId = React.useCallback((clientY) => {
    const outer = outerListRef.current;
    if (!outer) return null;
    const rect = outer.getBoundingClientRect();
    if (clientY < rect.top || clientY > rect.bottom) return null;
    const relativeY = clientY - rect.top + outer.scrollTop;
    const index = Math.floor(relativeY / virtualItemSize);
    return filteredMessages[index]?.contactId ?? null;
  }, [filteredMessages, virtualItemSize]);

  const handleListMouseMove = React.useCallback((event) => {
    lastMouseClientYRef.current = event.clientY;
    hoverStoreRef.current.setHoveredContactId(computeHoveredContactId(event.clientY));
  }, [computeHoveredContactId]);

  const handleListMouseLeave = React.useCallback(() => {
    lastMouseClientYRef.current = null;
    hoverStoreRef.current.setHoveredContactId(null);
  }, []);

  // Recompute on scroll too — scrolling moves which row sits under a
  // stationary cursor exactly the same way a reorder does, same underlying
  // "no real mouse event fired" issue.
  const handleListScroll = React.useCallback(() => {
    if (lastMouseClientYRef.current == null) return;
    hoverStoreRef.current.setHoveredContactId(computeHoveredContactId(lastMouseClientYRef.current));
  }, [computeHoveredContactId]);

  // The actual fix: re-derive hover from the LAST KNOWN mouse position
  // whenever the list itself reorders (not just when the mouse moves).
  // Without this, a reorder that slides a different contact under a
  // stationary cursor leaves the stale contact's row looking hovered (or
  // nothing hovered at all) until the user's mouse physically twitches —
  // the exact glitch Rahim reported (arrow/timestamp misfiring, lifecycle
  // chip's hover-tint flashing on the wrong row).
  React.useEffect(() => {
    if (lastMouseClientYRef.current == null) return;
    hoverStoreRef.current.setHoveredContactId(computeHoveredContactId(lastMouseClientYRef.current));
  }, [filteredMessages, computeHoveredContactId]);

  const liveMenuMessage = React.useMemo(() => {
    if (!menuOpenContactId) return null;
    return filteredMessages.find(msg => msg.contactId === menuOpenContactId);
  }, [menuOpenContactId, filteredMessages]);

  // Cached last-known value, not just the live lookup above — if the acted-on
  // contact briefly drops out of the currently-filtered/tab-scoped list (e.g.
  // reassigning it while viewing the "Unassigned" tab moves it out of that
  // filter the instant the action succeeds), the live lookup goes null for a
  // moment. MenuWithAnchor is only ever MOUNTED when this is truthy (see call
  // sites below), so that moment used to unmount the whole menu (submenus
  // included, since they're its children) and remount it once data settled —
  // and MUI's Popover flashes at (0,0) on a fresh mount before its position
  // calculation catches up, which is exactly the "glitches to top-left" bug
  // (Rahim, 2026-08-21). Keeping the last-known value alive across that gap,
  // and only really clearing it on explicit close, means the menu component
  // never unmounts for a reason the user didn't cause.
  const lastMenuMessageRef = React.useRef(null);
  if (liveMenuMessage) lastMenuMessageRef.current = liveMenuMessage;
  if (!menuOpenContactId) lastMenuMessageRef.current = null;
  const currentMenuMessage = liveMenuMessage || lastMenuMessageRef.current;

  const handleMessageClick = React.useCallback((message) => {
    if (message.isGroup) {
      selectGroup(message.contactId, { navigate: true, accountid, userid, clearPrevious: true });
      return;
    }
    selectContact(message.contactId, { navigate: true, accountid, userid, clearPrevious: true });
  }, [selectContact, selectGroup, accountid, userid]);

  const handleDeleteChat = React.useCallback(async (contactId) => {
    if (!await guard([])) return;
    const message = filteredMessages.find(msg => msg.contactId === contactId);
    setMenuAnchorPosition(null);
    setMenuOpenContactId(null);
    setConfirmDialog({ open: true, type: 'deleteChat', contactId, contactName: message?.name || 'this contact', isGroup: !!message?.isGroup });
  }, [filteredMessages, guard]);

  const handleDeleteContact = React.useCallback(async (contactId) => {
    if (!await guard([])) return;
    const message = filteredMessages.find(msg => msg.contactId === contactId);
    setMenuAnchorPosition(null);
    setMenuOpenContactId(null);
    setConfirmDialog({ open: true, type: 'deleteContact', contactId, contactName: message?.name || 'this contact' });
  }, [filteredMessages, guard]);

  const handleBlockContact = React.useCallback(async (contactId) => {
    if (!await guard([])) return;
    const message = filteredMessages.find(msg => msg.contactId === contactId);
    setMenuAnchorPosition(null);
    setMenuOpenContactId(null);
    // WhatsApp only: block_users requires the contact to have messaged within 24h (server
    // enforces this too — see blockContact.service.js). activeStatus is "hours remaining in
    // the 24h window" already delivered on every row (same field the Active tab filters on),
    // so skip the confirm dialog entirely and go straight to the error toast when it's expired,
    // instead of asking the user to confirm an action that's guaranteed to be rejected.
    const isWhatsApp = (message?.platform || 'whatsapp') === 'whatsapp';
    if (isWhatsApp && !(message?.activeStatus > 0)) {
      toast.error(
        'Cannot block this contact — WhatsApp only allows blocking contacts who have messaged your business within the last 24 hours.',
        { duration: 6000 }
      );
      return;
    }
    setConfirmDialog({ open: true, type: 'block', contactId, contactName: message?.name || 'this contact' });
  }, [filteredMessages, guard]);

  const unblockLockRef = React.useRef(false);
  const handleUnblockContact = React.useCallback(async (contactId) => {
    if (unblockLockRef.current) return;
    unblockLockRef.current = true;
    // Unblocking is only ever offered from the Blocked Contacts tab (see isBlockedTab in
    // MenuWithAnchor) — once unblocked, the contact no longer belongs in that tab's list, so
    // if its chat is open, close it out of the third pane too, same as block/delete.
    const wasViewingUnblockedContact = contactid && String(contactid) === String(contactId);
    const inboxUrl = accountid && userid ? `/home/${accountid}/inbox/${userid}` : null;
    try {
      const result = await dispatch(unblockContact(contactId)).unwrap();
      if (result?.warning) {
        toast(result.warning, { icon: '⚠️', duration: 6000 });
      } else {
        toast.success('Contact unblocked');
      }
      if (wasViewingUnblockedContact && inboxUrl) {
        recentlyDeletedContactRef.current = String(contactId);
        if (recentlyDeletedClearTimerRef.current) clearTimeout(recentlyDeletedClearTimerRef.current);
        recentlyDeletedClearTimerRef.current = setTimeout(() => {
          recentlyDeletedContactRef.current = null;
          recentlyDeletedClearTimerRef.current = null;
        }, 5000);
        dispatchInboxSidebarTabChangeClear(dispatch);
        const effectiveUserid = userid || userDetails?._id;
        navigateOffCustomerChatRoute(navigate, accountid, effectiveUserid);
      }
    } catch (e) {
      console.error("Unblock failed", e);
      toast.error(typeof e === 'string' && e ? e : 'Failed to unblock contact. Please try again.', { duration: 6000 });
    } finally {
      setTimeout(() => { unblockLockRef.current = false; }, 200);
    }
  }, [dispatch, contactid, accountid, userid, userDetails?._id, navigate]);

  const handleConfirmDelete = React.useCallback(async () => {
    const { type, contactId, isGroup: isGroupTarget } = confirmDialog;
    setConfirmDialog({ open: false, type: null, contactId: null, contactName: null, isGroup: false });
    // Name is delete-era, but the check (and the close-out-of-chatbox logic below) is generic:
    // "was this contact's chat open when the action was confirmed" — also used for block.
    const wasViewingDeletedContact = contactid && String(contactid) === String(contactId);
    const inboxUrl = accountid && userid ? `/home/${accountid}/inbox/${userid}` : null;
    try {
      if (type === 'deleteChat') {
        // A group's "Delete Chat" never reaches 'deleteContact' (that menu item is hidden
        // for a group row -- see MenuWithAnchor's isGroup branch), so only this one branch
        // needs the group/contact split.
        await dispatch(isGroupTarget ? deleteGroupChat(contactId) : deleteChat(contactId)).unwrap();
      } else if (type === 'deleteContact') {
        await dispatch(deleteContact(contactId)).unwrap();
      } else if (type === 'block') {
        const result = await dispatch(blockContact(contactId)).unwrap();
        // metaApiWarning: local block succeeded but the messaging provider couldn't confirm
        // it (e.g. Instagram/Facebook token issue) — surface it instead of staying silent.
        if (result?.warning) {
          toast(result.warning, { icon: '⚠️', duration: 6000 });
        } else {
          toast.success('Contact blocked');
        }
        // If this contact's chat is currently open, close it out of the third pane below —
        // same as deleteChat/deleteContact — instead of leaving a blocked contact's thread
        // sitting open (composer-lock refresh is moot once we navigate away from it).
      }
      if ((type === 'deleteChat' || type === 'deleteContact' || type === 'block') && wasViewingDeletedContact && inboxUrl) {
        recentlyDeletedContactRef.current = String(contactId);
        if (recentlyDeletedClearTimerRef.current) clearTimeout(recentlyDeletedClearTimerRef.current);
        recentlyDeletedClearTimerRef.current = setTimeout(() => {
          recentlyDeletedContactRef.current = null;
          recentlyDeletedClearTimerRef.current = null;
        }, 5000);
        dispatchInboxSidebarTabChangeClear(dispatch);
        const effectiveUserid = userid || userDetails?._id;
        navigateOffCustomerChatRoute(navigate, accountid, effectiveUserid);
      }
    } catch (error) {
      console.error(`Failed to ${type}:`, error);
      dispatch(fetchInbox());
      if (type === 'block') {
        // error is the rejectWithValue string from blockContact (e.g. the WhatsApp
        // 24-hour-window message) — show it verbatim instead of a generic fallback.
        toast.error(typeof error === 'string' && error ? error : 'Failed to block contact. Please try again.', { duration: 6000 });
      } else {
        const actionLabel = type === 'deleteChat' ? 'delete chat' : 'delete contact';
        alert(`Failed to ${actionLabel}. Please try again.`);
      }
    }
  }, [confirmDialog, dispatch, contactid, accountid, userid, userDetails?._id, navigate]);

  React.useEffect(() => {
    return () => {
      if (recentlyDeletedClearTimerRef.current) clearTimeout(recentlyDeletedClearTimerRef.current);
    };
  }, []);

  const handleCloseConfirmDialog = React.useCallback(() => {
    setConfirmDialog({ open: false, type: null, contactId: null, contactName: null, isGroup: false });
  }, []);

  const handleLifecycleChanged = React.useCallback((contactId, newStageId) => {
    dispatch(updateLifecycleStageInInbox({ contactId, lifecycleStageId: newStageId }));
  }, [dispatch]);

  const pinLockRef = React.useRef(false);
  const handleTogglePin = React.useCallback(async (contactId) => {
    if (pinLockRef.current) return;
    pinLockRef.current = true;
    // Same "look up the full row from the currently-filtered list" pattern
    // handleDeleteChat above already uses -- this function is only ever called with the
    // bare id, so isGroup has to be resolved here rather than threaded through as an arg.
    const message = filteredMessages.find((msg) => msg.contactId === contactId);
    try {
      await dispatch(message?.isGroup ? toggleGroupPin(contactId) : togglePinContact(contactId)).unwrap();
    } catch (e) {
      console.error("Pin failed", e);
    } finally {
      setTimeout(() => { pinLockRef.current = false; }, 200);
    }
  }, [dispatch, filteredMessages]);

  // renderRow no longer has menuOpenContactId in deps — that state lives in context.
  // Only re-renders when selection, data, or stable callbacks change.
  // Team ids (from the same inboxSettings teams list the Inbox sidebar's "Teams" section
  // loads) — lets a row tell a team assignee from a person for the status chip's label,
  // since the card data itself only carries the assignee's id and name.
  const inboxTeams = useSelector((s) => s.inboxSettings?.teams);
  const teamIdSet = React.useMemo(
    () => new Set((Array.isArray(inboxTeams) ? inboxTeams : []).map((t) => String(t.id || t._id))),
    [inboxTeams]
  );

  const renderRow = React.useCallback(({ index, style }) => {
    const message = filteredMessages[index];
    if (!message) return null;
    const rowCid = String(message.contactId);
    const isRowSelected =
      rowCid === String(selectedContactId) ||
      (openingCustomerContactId != null && rowCid === String(openingCustomerContactId));

    return (
      <MemoizedMessageItem
        message={message}
        index={index}
        style={style}
        isSelected={isRowSelected}
        onMessageClick={handleMessageClick}
        onDeleteChat={handleDeleteChat}
        onDeleteContact={handleDeleteContact}
        onTogglePin={handleTogglePin}
        onMenuOpen={handleMenuOpen}
        onLifecycleChanged={handleLifecycleChanged}
        bulkSelectable={!isBlockedTab}
        assigneeIsTeam={teamIdSet.has(String(message.assignedToId || message.assignedTo || ""))}
      />
    );
  }, [
    isBlockedTab,
    teamIdSet,
    filteredMessages,
    selectedContactId,
    openingCustomerContactId,
    handleMessageClick,
    handleDeleteChat,
    handleDeleteContact,
    handleTogglePin,
    handleMenuOpen,
    handleLifecycleChanged,
  ]);

  const hasAnyInboxData = Boolean(
    data &&
    Object.values(data).some((entry) => Array.isArray(entry) && entry.length > 0)
  );

  if (loading && !hasAnyInboxData) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2, padding: 2 }}>
        {[1, 2, 3, 4, 5, 6].map((item) => (
          <Box key={item} sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Skeleton variant="circular" width={40} height={40} />
            <Skeleton variant="text" width="80%" height={20} />
          </Box>
        ))}
      </Box>
    );
  }

  if (error) {
    if (error.includes("Authentication required") || error.includes("Invalid token")) return null;
    return <p>{getErrorMessage(error)}</p>;
  }

  if (!loading && initialized && (!filteredMessages || filteredMessages.length === 0)) {
    // While a real search is in flight, this same spot shows a spinner
    // instead of "No conversations match your search" -- contactsData is
    // empty at this point (InboxAll.jsx deliberately returns nothing until
    // the DB call resolves, see that file's contactsToShow), so without this
    // branch the empty-state message would flash misleadingly before the
    // real results (or the real "nothing found") land (2026-09-08).
    if (isSearching) {
      return (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            height: "100%",
            textAlign: "center",
            padding: 0,
          }}
        >
          <CircularProgress size={32} sx={{ mb: 2, color: (theme) => theme.palette.text.secondary }} />
          <Typography variant="h6" color="text.secondary">
            Searching…
          </Typography>
        </Box>
      );
    }
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
          textAlign: "center",
          padding: 0,
        }}
      >
        {/* Iconsax, our own standard — dark mode needs a fixed white here,
            not "gray", or it reads near-invisible on the dark background
            (Rahim, 2026-08-31: "use white for dark mood"). */}
        <Box sx={{ mb: 2, color: (theme) => theme.palette.text.secondary, display: "flex" }}>
          <svg xmlns="http://www.w3.org/2000/svg" width="58" height="58" viewBox="0 0 24 24" fill="none">
            <g>
              <path d="M7 12C3 12 3 13.79 3 16V17C3 19.76 3 22 8 22H16C20 22 21 19.76 21 17V16C21 13.79 21 12 17 12C16 12 15.72 12.21 15.2 12.6L14.18 13.68C13 14.94 11 14.94 9.81 13.68L8.8 12.6C8.28 12.21 8 12 7 12Z" stroke="currentColor" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M19 12V6C19 3.79 19 2 15 2H9C5 2 5 3.79 5 6V12" stroke="currentColor" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M10.55 9.23047H13.88" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M9.71997 6.23047H14.72" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </g>
          </svg>
        </Box>
        {/* Was checking activeFilter === 'unreplied', a value that never
            actually occurs — Unreplied is its own separate unrepliedOnly
            boolean (InboxAll.jsx), never threaded through as a prop here, so
            that branch was dead and silently fell through to the generic
            fallback (Rahim, 2026-08-31: "no all calls it really doesnt make
            sense fix for both chats and calls for all type of selected
            messages" — same bug shape as Calls' "No all calls"). Search
            checked first since it's the most specific "you looked for
            something and got nothing" case, ahead of whichever filter/label
            happens to also be active. */}
        <Typography variant="h6" color="text.secondary">
          {searchTerm
            ? "No conversations match your search"
            : unrepliedOnly
            ? "No unreplied conversations found"
            : activeFilter === 'opened'
            ? "No opened conversations found"
            : activeFilter === 'unread'
            ? "No unread messages found"
            : selectedLabels.length > 0
            ? "No messages found with selected labels"
            : "No conversations found"}
        </Typography>
      </Box>
    );
  }

  const confirmDialogNode = (
    <Dialog
      open={confirmDialog.open}
      onClose={handleCloseConfirmDialog}
      aria-labelledby="confirm-dialog-title"
      aria-describedby="confirm-dialog-description"
      // Matches InboxAll.jsx's "Create New Contact" dialog scale exactly —
      // was a mix of the older "Reports' Customize Columns" formula (fixed
      // 1.2rem title, non-responsive padding) and a boxed CloseSquare icon,
      // inconsistent with every other Inbox dialog (Rahim, 2026-08-31:
      // "make it properly aligned to our new theme of inbox").
      // PaperProps, not slotProps — this installed MUI version's Dialog
      // component (node_modules/@mui/material/Dialog/Dialog.js) only reads
      // PaperProps; slotProps.paper is silently ignored, so none of this
      // sx was ever actually applying (Rahim, 2026-08-31: "its still very
      // pointy" — real bug, not a stale-cache/wrong-dialog issue).
      PaperProps={{
        sx: {
          borderRadius: "16px",
          boxShadow: (theme) => (theme.palette.mode === "dark" ? "0 8px 24px rgba(0,0,0,0.4)" : "0 8px 24px rgba(0,0,0,0.15)"),
          backgroundColor: (theme) => theme.palette.background.paper,
        },
      }}
    >
      <DialogTitle
        id="confirm-dialog-title"
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          fontWeight: 600,
          fontSize: { xs: "0.9375rem", sm: "1rem", md: "1.05rem", lg: "1.05rem", xl: "1.05rem" },
          padding: { xs: "12px 16px", sm: "14px 18px", md: "16px 20px", lg: "16px 20px", xl: "16px 20px" },
          marginBottom: 0,
          borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
          backgroundColor: (theme) => theme.palette.background.paper,
        }}
      >
        <span>
          {confirmDialog.type === 'deleteChat'
            ? 'Delete Chat'
            : confirmDialog.type === 'block'
            ? 'Block Contact'
            : 'Delete Contact'}
        </span>
        {/* Plain CloseIcon + hover, matching InboxAll.jsx's "Create New
            Contact" dialog convention — was iconsax's CloseSquare (a
            boxed/bordered X), the odd one out among Inbox's dialogs (Rahim,
            2026-08-31: "the cross is not good do it like other pop ups in
            the inbox page"). */}
        <IconButton
          size="small"
          aria-label="Close dialog"
          onClick={handleCloseConfirmDialog}
          sx={{
            color: (theme) => theme.palette.text.secondary,
            "&:hover": { backgroundColor: (theme) => theme.palette.action.hover, color: (theme) => theme.palette.text.primary },
            "& .MuiSvgIcon-root": { fontSize: { xs: "18px", sm: "20px", md: "22px", lg: "22px", xl: "22px" } },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent
        sx={{
          px: { xs: 2, sm: 2.5, md: 3, lg: 3, xl: 3 },
          pb: { xs: 2, sm: 2.5, md: 2.5, lg: 2.5, xl: 2.5 },
          backgroundColor: (theme) => theme.palette.background.paper,
        }}
      >
        {/* pt lives here, not on DialogContent itself — MUI forces
            `.MuiDialogTitle-root + .MuiDialogContent-root { padding-top: 0 }`
            (a sibling-selector rule that beats a plain sx pt override),
            same reason InboxAll.jsx's Create Contact dialog wraps its
            content in an inner Box too (Rahim, 2026-08-31: "no proper
            spacing for the sub para" — was sitting flush against the
            divider under the title). */}
        <Box sx={{ pt: { xs: 2.5, sm: 3, md: 3.25, lg: 3.25, xl: 3.25 } }}>
          <DialogContentText id="confirm-dialog-description" sx={{ fontSize: "0.875rem", color: "text.secondary" }}>
            {confirmDialog.type === 'deleteChat'
              ? `Are you sure you want to delete the chat with ${confirmDialog.contactName}? The contact will remain, but all chat history will be deleted.`
              : confirmDialog.type === 'block'
              ? `Are you sure you want to block ${confirmDialog.contactName}? They won't be able to message you, and you won't be able to message them until you unblock this contact.`
              : `Are you sure you want to delete ${confirmDialog.contactName}? The contact and all chat history will be deleted.`}
          </DialogContentText>
        </Box>
      </DialogContent>
      <DialogActions
        sx={{
          padding: { xs: "12px 16px 16px", sm: "14px 20px 18px", md: "16px 20px 20px", lg: "16px 20px 20px", xl: "16px 20px 20px" },
          justifyContent: "space-between",
          borderTop: (theme) => `1px solid ${theme.palette.divider}`,
          backgroundColor: (theme) => theme.palette.background.paper,
        }}
      >
        <Button
          onClick={handleCloseConfirmDialog}
          sx={{
            color: (theme) => theme.palette.text.secondary,
            textTransform: "none",
            fontSize: "0.8125rem",
            fontWeight: 500,
            borderRadius: "8px",
            px: 1.25,
            "&:hover": { backgroundColor: (theme) => theme.palette.action.hover },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleConfirmDelete}
          color="error"
          variant="contained"
          autoFocus
          disableElevation
          sx={{
            borderRadius: "8px",
            textTransform: "none",
            fontSize: "0.8125rem",
            fontWeight: 600,
            px: { xs: 1.375, sm: 1.75 },
            py: { xs: 0.625, sm: 0.75 },
          }}
        >
          {confirmDialog.type === 'block' ? 'Block' : 'Delete'}
        </Button>
      </DialogActions>
    </Dialog>
  );

  try {
    return (
      // Provide menuOpenContactId via context — only the row whose contactId matches re-renders
      <MenuOpenContext.Provider value={menuOpenContactId}>
        {/* HoveredRowContext -- see its own comment (top of file) for why this
            is computed from real mouse coordinates instead of CSS :hover,
            and why it carries a STABLE STORE OBJECT (hoverStoreRef.current)
            rather than the hoveredContactId value itself -- providing the
            value directly would re-render every visible row on each hover
            change; the store lets each row subscribe individually instead.
            onMouseMove/onMouseLeave here handle actual cursor movement;
            onScroll+the reorder-driven effect above (see filteredMessages
            effect near computeHoveredContactId) re-derive it from the last
            known position when the ROWS move instead of the mouse. */}
        <HoveredRowContext.Provider value={hoverStoreRef.current}>
        <Box
          ref={containerRefCallback}
          onMouseMove={handleListMouseMove}
          onMouseLeave={handleListMouseLeave}
          sx={{
            flex: 1,
            minHeight: 0,
            width: "100%",
            overflow: "hidden",
            position: "relative",
          }}
        >
          <VirtualList
            height={containerHeight}
            itemCount={filteredMessages.length}
            itemSize={virtualItemSize}
            width="100%"
            overscanCount={2}
            itemKey={(index) => filteredMessages[index]?.contactId || index}
            outerRef={outerListRef}
            onScroll={handleListScroll}
            style={{ backgroundColor: 'transparent' }}
            onItemsRendered={({ visibleStopIndex }) => {
              const threshold = Math.max(0, filteredMessages.length - 5);
              if (
                hasMore &&
                !isLoadingMore &&
                !loadMorePendingRef.current &&
                onLoadMore &&
                filteredMessages.length > 0 &&
                visibleStopIndex >= threshold
              ) {
                loadMorePendingRef.current = true;
                onLoadMore();
              }
            }}
          >
            {renderRow}
          </VirtualList>
        </Box>

        {showLoadMoreButton && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 1, borderTop: (theme) => `1px solid ${theme.palette.divider}`, height: "48px", minHeight: "48px", maxHeight: "48px" }}>
            <Button
              size="small"
              variant="outlined"
              onClick={onExplicitLoadMore}
              disabled={isLoadingMore}
              sx={{ textTransform: "none", fontSize: "0.75rem", borderRadius: "16px", px: 2.5 }}
            >
              {isLoadingMore ? "Loading…" : "More"}
            </Button>
          </Box>
        )}

        {currentMenuMessage && (
          <MenuWithAnchor
            anchorPosition={menuAnchorPosition}
            menuOpenContactId={menuOpenContactId}
            currentMenuMessage={currentMenuMessage}
            onClose={handleMenuClose}
            onTogglePin={handleTogglePin}
            onDeleteChat={handleDeleteChat}
            onDeleteContact={handleDeleteContact}
            onBlockContact={handleBlockContact}
            onUnblockContact={handleUnblockContact}
            onLifecycleChanged={handleLifecycleChanged}
            isBlockedTab={isBlockedTab}
          />
        )}

        {confirmDialogNode}
        </HoveredRowContext.Provider>
      </MenuOpenContext.Provider>
    );
  } catch (error) {
    console.warn('Virtualization failed, falling back to regular list:', error);
    return (
      <MenuOpenContext.Provider value={menuOpenContactId}>
        {/* Not virtualized here (react-window itself threw) -- no reorder-
            under-a-stationary-cursor problem to solve, so no mouse handlers
            wired up; NOOP_HOVER_STORE means no row ever reads as hovered
            via JS here (see its own comment, top of file). */}
        <HoveredRowContext.Provider value={NOOP_HOVER_STORE}>
        <>
          <List sx={{ display: "flex", flexDirection: "column", overflowY: "auto", padding: 0, height: "100%" }}>
            {filteredMessages.map((message, index) => {
              const rowCid = String(message.contactId);
              const isRowSelected =
                rowCid === String(selectedContactId) ||
                (openingCustomerContactId != null && rowCid === String(openingCustomerContactId));
              return (
                <MemoizedMessageItem
                  key={message.contactId || index}
                  message={message}
                  index={index}
                  style={{}}
                  isSelected={isRowSelected}
                  onMessageClick={handleMessageClick}
                  onDeleteChat={handleDeleteChat}
                  onDeleteContact={handleDeleteContact}
                  onTogglePin={handleTogglePin}
                  onMenuOpen={handleMenuOpen}
                  onLifecycleChanged={handleLifecycleChanged}
                />
              );
            })}
          </List>

          {currentMenuMessage && (
            <MenuWithAnchor
              anchorPosition={menuAnchorPosition}
              menuOpenContactId={menuOpenContactId}
              currentMenuMessage={currentMenuMessage}
              onClose={handleMenuClose}
              onTogglePin={handleTogglePin}
              onDeleteChat={handleDeleteChat}
              onDeleteContact={handleDeleteContact}
              onBlockContact={handleBlockContact}
              onUnblockContact={handleUnblockContact}
              isBlockedTab={isBlockedTab}
            />
          )}

          {confirmDialogNode}
        </>
        </HoveredRowContext.Provider>
      </MenuOpenContext.Provider>
    );
  }
};

export default MessageCard;
